import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { NewClassGet, TagsGet, Teacher as TeacherModel, YearGroupInterface } from '@whizz/api-model';
import { Translate, useI18n } from '@whizz/react-i18n';
import { client, model, repository } from '@whizz/api-client';
import { AssessmentStartAgeDelta, RosterManagementStudentActionEnum } from '@whizz/api-model';
import { useQuery, useQueryClient } from 'react-query';
import Modal, { ModalFooter } from '../../../../components/modal/Modal';
import { Button } from '../../../../components';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import { useIdentity } from '@whizz/react-identity';
import { APP_THEME } from '../../../../styles/theme/styled/theme';
import { transparentize } from 'polished';

const INPUT_SM = 'w-full text-sm h-8 leading-8 px-2 py-0 border border-gray-300 rounded';
const ICON_BTN = '!h-8 !w-8 !p-0 grid place-items-center rounded';
const ROW_HEIGHT = 64;
const ROW_ERROR_BACKGROUND_COLOR = transparentize(0.7, APP_THEME.colors.error);
const ROW_EDIT_BACKGROUND_COLOR = transparentize(0.5, APP_THEME.colors.info);
const OVERSCAN = 12;
const MAX_WINDOWED_ROWS = 120;
const COLSPAN_STUDENTS = 16;
const COLSPAN_TEACHERS = 12;
const COLSPAN_PARENTS = 11;
const ASSESSMENT_DELTA_OPTIONS = Array.from({ length: 10 }, (_, i) => String(i + 1));
const DEFAULT_PASSWORD_PREFIX = 'whizz';
const HIDDEN_CLASS_SUGGESTION_NAMES = new Set([ 'archive' ]);
/*
  TODO: identify the issue where every 3rd batch fails and has to be retried (especially on large uploads). currently its patched by adding delays between batches.
  and retrying failed batches with exponential backoff.

*/
type UploadTabs = 'students' | 'teachers' | 'parents';

type StudentUploadPageProps = {
  initialTab?: UploadTabs;
  lockTab?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  autoOpenFilePicker?: boolean;
  onAutoOpenFilePicker?: () => void;
};

type ConfirmOptions = {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
};

type BulkUploadActivationSummary = {
  activated?: number;
  inactive?: number;
};

type BulkUploadMessage = {
  title?: string;
  titleKey?: string;
  accountsLine?: string;
  activeLine?: string;
  remainingLine?: string | null;
  supportLine?: string | null;
  accounts?: {
    count?: number;
  };
  active?: {
    count?: number;
  };
  remaining?: string | null;
  support?: string | null;
};

type BulkUploadResponseSummary = {
  succeeded?: number;
  failed?: number;
  activation?: BulkUploadActivationSummary;
  message?: BulkUploadMessage;
};

type TagInputProps = {
  value: string;
  onChange: (v: string) => void;
  allTags: string[];
  placeholder?: string;
  appendCommaOnPick?: boolean;
  onCommit?: () => void;
};

type StudentBulkRow = {
  upn: string;
  firstName: string;
  lastName: string;
  dob: string;
  yearGroupId: number | null;
  gender: number;
  password: string;
  username: string;
  active: boolean;
  archive?: boolean;
  classesIds: number[];
  tagsIds: number[];
  aboveBelowAssessmentDelta: number;
  assessmentDelta: number;
};

type TeacherBulkRow = {
  firstName: string;
  lastName: string;
  jobType: number;
  jobTitle: string | null;
  email: string | null;
  recoveryEmail: string | null;
  password: string | null;
  username: string | null;
  phone: string;
  isAdmin: boolean;
  cleverId: string | null;
  countryId: number | null;
  ssoAccessId: number | null;
  ssoClientId: number | null;
  classIds: number[];
  emailClassIds: number[];
};

type GuardianRow = {
  upn: string;
  childId: number;
  childName: string;
  guardians: Array<{ name: string; email: string }>;
};

type EmailCellProps = {
  rowId: string;
  isEditing: boolean;
  value: string;
  field: keyof Parent;
  bad: boolean;
  title: string;
  onChange: (id: string, field: keyof Parent, v: string) => void;
  onBlurNormalize: (id: string, field: keyof Parent, v: string) => void;
};

type BackendTag = {
  id: number;
  abbreviation: string;
  description?: string;
  color?: number;
  isDefault?: boolean | number;
};

type ExistingStudentUsernameCatalog = {
  existingUsernames: Set<string>;
  upnToUsername: Record<string, string>;
};

type WindowedRows<T> = {
  topPad: number;
  bottomPad: number;
  rows: T[];
};

type YearGroup = {
  id: number;
  yearGroupId:number | null;
  code: string;
  name: string;
};

type ProgressPhase = 'idle' | 'preparing' | 'saving';
type PreflightDicts = { tagDict: Record<string, number>; classDict: Record<string, number> };
type ClassPreflightDicts = Pick<PreflightDicts, 'classDict'>;
type DeltaOption = { label: string; value: AssessmentStartAgeDelta };
type UploadSummary = {
  total: number;
  valid: number;
  errors: number;
  newStudents: number;
  updatingStudents: number;
  uploadLabelSingularKey: string;
  uploadLabelPluralKey: string;
};
interface Student {
  id: string;
  upn: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  yearGroup: string;
  gender: string;
  password: string;
  username: string;
  licenseActive: boolean;
  archive?: boolean;
  className: string;
  tagName: string;
  aboveBelowAssessmentDelta: string;
  assessmentDelta: AssessmentStartAgeDelta | string | '';
}
interface Teacher {
  id: string;
  jobType: string;
  name: string;
  lastName: string;
  email: string;
  RecoveryEmail: string
  classes: string;
  emailClasses: string;
  phone: string;
  admin: boolean;
  error?: string;
}
interface Parent {
  id: string;
  upn: string;
  studentName: string;
  g1Name: string;
  g1Email: string;
  g2Name: string;
  g2Email: string;
  g3Name: string;
  g3Email: string;
  error?: string;
}

type UIState = {
  alert: { open: boolean; title?: string | null; body: React.ReactNode | null };
  confirm: {
    open: boolean;
    title: string;
    body: React.ReactNode | null;
    opts: ConfirmOptions;
  };
};

type UIAction =
  | { type: 'ALERT_OPEN'; title?: string | null; body: React.ReactNode }
  | { type: 'ALERT_CLOSE' }
  | { type: 'CONFIRM_OPEN'; title?: string; body: React.ReactNode; opts?: ConfirmOptions }
  | { type: 'CONFIRM_CLOSE' };

type CatalogsState = {
  tags: { names: string[]; idByAbbrev: Record<string, number> };
  classes: { names: string[]; idByName: Record<string, number> };
  yearGroups: { list: YearGroup[]; codeToId: Record<string, number>; codeToName: Record<string, string> };
};

const EMPTY_TAG_CATALOG: CatalogsState['tags'] = { names: [], idByAbbrev: {} };
const EMPTY_CLASS_CATALOG: CatalogsState['classes'] = { names: [], idByName: {} };
const EMPTY_YEAR_GROUP_CATALOG: CatalogsState['yearGroups'] = { list: [], codeToId: {}, codeToName: {} };

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case 'ALERT_OPEN':
      return { ...state, alert: { open: true, title: action.title ?? null, body: action.body } };
    case 'ALERT_CLOSE':
      return { ...state, alert: { open: false, title: null, body: null } };
    case 'CONFIRM_OPEN':
      return {
        ...state,
        confirm: {
          open: true,
          title: action.title ?? '',
          body: action.body,
          opts: action.opts ?? {},
        },
      };
    case 'CONFIRM_CLOSE':
      return { ...state, confirm: { open: false, title: '', body: null, opts: {} } };
    default:
      return state;
  }
}

const RowButton: React.FC<React.ComponentProps<typeof Button> & { widthClass?: string }> = ({ children, className = '', widthClass = 'w-20', ...props }) => (
  <Button
    data-id="whizz_button"
    color="positive"
    isOutline
    className={`${widthClass} !h-6 !py-0 !px-2 !text-[13px] !leading-none ${className}`}
    {...props}
  >
    {children}
  </Button>
);

const AddRowButton = ({
  children,
  onClick,
}: {
  onClick: () => void;
  children?: React.ReactNode;
}) => {
  const { translate } = useI18n();
  return (
    <RowButton onClick={onClick} widthClass="w-24">
      {children ?? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.ADD_ROW')}
    </RowButton>
  );
};

const DeleteRowButton = ({
  rowId,
  onDelete,
}: {
  rowId: string;
  onDelete: (id: string) => void | Promise<void>;
}) => {
  const { translate } = useI18n();
  const label = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DELETE');

  return (
    <Button
      data-id="whizz_button"
      color={'transparent'}
      className={`${ICON_BTN} ml-2`}
      icon={GLOBAL_ICONS.delete}
      onClick={() => { void onDelete(rowId); }}
      title={label}
    >
      <span className="sr-only">{label}</span>
    </Button>
  );
};

const TranslateWithSpan = ({ i18nKey }: { i18nKey: string }) => (
  <Translate i18nKey={i18nKey}>
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <span />
  </Translate>
);

const makeDefaultPassword = () => {
  const n = Math.floor(Math.random() * 100);
  return `${DEFAULT_PASSWORD_PREFIX}${String(n).padStart(2, '0')}`;
};

/** Only trigger global Enter/Escape handlers when NOT typing in a text field. */
function isTypingInInput(evt: KeyboardEvent) {
  const t = evt.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName.toLowerCase();
  const isEditable =
    (t as any).isContentEditable ||
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select';
  return isEditable;
}

const EmailCell: React.FC<EmailCellProps> = memo(
  ({ rowId, isEditing, value, field, bad, title, onChange, onBlurNormalize }) => (
    <td className="border border-gray-400">
      {isEditing ? (
        <div className="flex items-center h-6">
          <input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={`w-full text-sm px-1 py-0.5 h-6 border ${bad ? 'border-red-500' : 'border-gray-300'}`}
            value={value}
            onChange={(e) => onChange(rowId, field, e.target.value)}
            onBlur={(e) => onBlurNormalize(rowId, field, e.target.value)}
            title={bad ? 'Enter a valid email like name@example.com' : ''}
          />
          {bad && <span className="ml-1 text-xs leading-none" title={title}>?</span>}
        </div>
      ) : (
        <div className="flex items-center px-1 min-w-0">
          <span className="block flex-1 truncate" title={value}>
            {value}
          </span>
          {bad && <span className="ml-1 text-xs flex-shrink-0" title={title}>?</span>}
        </div>
      )}
    </td>
  ),
);

const RowErrorsMemo = memo(function RowErrorsMemo({
  errors,
}: {
  errors: string[];
}) {
  return <>{errors.join('; ')}</>;
});

const startTransitionSafe = (cb: () => void) => {
  const st = (React as any).startTransition;
  if (typeof st === 'function') st(cb);
  else cb(); // fallback for React < 18
};

const EMPTY_EXISTING_STUDENT_USERNAMES = new Set<string>();
const EMPTY_UPN_TO_USERNAME: Record<string, string> = {};

function normalizeTags(input: unknown): BackendTag[] {
  // TIAAN--- Accept a few common shapes: array, { tags: [ ... ] }, { data: [ ... ] }
  const arr: unknown[] =
    Array.isArray(input)
      ? input
      : Array.isArray((input as any)?.tags)
        ? (input as any).tags
        : Array.isArray((input as any)?.data)
          ? (input as any).data
          : [];

  return (arr as any[]).map((t) => {
    const abbr =
      (t?.abbreviation ??
        t?.name ??
        t?.title ??
        t?.label ??
        '').toString().trim();

    return {
      id: Number(t?.id),
      abbreviation: abbr,
      description: t?.description ? String(t.description) : undefined,
      color: Number.isFinite(Number(t?.color)) ? Number(t.color) : undefined,
      isDefault:
        typeof t?.isDefault === 'boolean'
          ? t.isDefault
          : (Number.isFinite(Number(t?.isDefault)) ? Number(t.isDefault) : undefined),
    };
  }).filter((t) => t.abbreviation.length > 0);
}

function buildTagCatalog(tagsResponse: TagsGet): CatalogsState['tags'] {
  const rawTags = normalizeTags(tagsResponse);
  const names = Array.from(new Set(rawTags.map((t) => t.abbreviation))).sort((a, b) => a.localeCompare(b));
  const idByAbbrev: Record<string, number> = {};
  rawTags.forEach((tag) => { idByAbbrev[tag.abbreviation.toLowerCase()] = tag.id; });

  return { names, idByAbbrev };
}

function buildClassCatalog(classList: NewClassGet): CatalogsState['classes'] {
  const classes = Array.isArray(classList) ? classList : [];
  const namesSet = new Set<string>();
  const idByName: Record<string, number> = {};

  classes.forEach((classItem) => {
    const { id: rawId, name: rawName } = classItem as { id?: unknown; name?: unknown };
    const name = String(rawName ?? '').trim();
    if (!name) return;

    namesSet.add(name);

    const id = Number(rawId);
    if (Number.isFinite(id)) {
      idByName[name.toLowerCase()] = id;
    }
  });

  return {
    names: Array.from(namesSet).sort((a, b) => a.localeCompare(b)),
    idByName,
  };
}

function buildYearGroupCatalog(yearGroups: YearGroupInterface[]): CatalogsState['yearGroups'] {
  const list = Array.isArray(yearGroups) ? yearGroups as YearGroup[] : [];
  const codeToId: Record<string, number> = {};
  const codeToName: Record<string, string> = {};

  list.forEach((yearGroup) => {
    const code = String(yearGroup.code).toUpperCase();
    const payloadId =
      Number.isFinite(Number(yearGroup.yearGroupId)) ? Number(yearGroup.yearGroupId) :
        Number.isFinite(Number(yearGroup.id)) ? Number(yearGroup.id) :
          NaN;

    if (Number.isFinite(payloadId)) {
      codeToId[code] = payloadId;
    }

    codeToName[code] = String(yearGroup.name);
  });

  return { list, codeToId, codeToName };
}

const usernameKey = (u?: string | null) => String(u ?? '').trim().toLowerCase();

type LocalizedGenderAliases = {
  male?: string[];
  female?: string[];
  other?: string[];
};

const normalizeGenderUI = (
  raw?: string | null,
  localizedAliases: LocalizedGenderAliases = {},
): '' | 'M' | 'F' | 'Other' => {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return '';
  if ([ '1', 'm', 'male', 'boy', ...(localizedAliases.male ?? []) ].includes(s)) return 'M';
  if ([ '2', 'f', 'female', 'girl', 'fem', ...(localizedAliases.female ?? []) ].includes(s)) return 'F';
  if ([ '3', 'o', 'other', 'x', 'nb', 'nonbinary', 'non-binary', ...(localizedAliases.other ?? []) ].includes(s)) return 'Other';
  return '';
};

const JOB_TYPE_DEFS = [
  { id: 0, label: 'Teacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.TEACHER', aliases: [ 'teacher' ] },
  { id: 1, label: 'Principal/Head teacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.PRINCIPAL', aliases: [ 'principal', 'head', 'headteacher' ] },
  { id: 2, label: 'Deputy/Assistant Principal/Headteacher', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.DEPUTY', aliases: [ 'deputy', 'assistantprincipal', 'assistantheadteacher' ] },
  { id: 3, label: 'Maths Lead', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.MATHS_LEAD', aliases: [ 'mathslead', 'mathlead', 'mathematicslead' ] },
  { id: 4, label: 'Senior Leadership team member', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.SENIOR_LEADERSHIP', aliases: [ 'slt', 'seniorleadership', 'seniorleadershipteammember' ] },
  { id: 5, label: 'Teaching Assistant', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.ASSISTANT', aliases: [ 'ta', 'teachingassistant' ] },
  { id: 6, label: 'Finance', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.FINANCE', aliases: [ 'finance' ] },
  { id: 7, label: 'Other', key: 'pages.ROSTER_MANAGEMENT.STAFF.JOB_TITLES.OTHER', aliases: [ 'other' ] },
] as const;

type TranslateFn = (key: string, fallback?: string) => string;
type JobTypeLookups = {
  jobTypes: Array<{ id: number; label: string }>;
  labelById: Record<number, string>;
  idByCanon: Record<string, number>;
};

const canon = (s: string) => s.trim().toLowerCase().replace(/[\s/-]+/g, '');
const buildJobTypeLookups = (translateFn?: TranslateFn): JobTypeLookups => {
  const jobTypes = JOB_TYPE_DEFS.map((jobType) => ({
    id: jobType.id,
    label: String(translateFn ? translateFn(jobType.key, jobType.label) : jobType.label),
  }));

  const labelById = Object.fromEntries(jobTypes.map((jobType) => [ jobType.id, jobType.label ])) as Record<number, string>;
  const idByCanon: Record<string, number> = {};

  JOB_TYPE_DEFS.forEach((jobType) => {
    const candidates = [ jobType.label, labelById[jobType.id], ...jobType.aliases ];
    candidates.filter(Boolean).forEach((candidate) => {
      idByCanon[canon(String(candidate))] = jobType.id;
    });
  });

  return { jobTypes, labelById, idByCanon };
};

const DEFAULT_JOB_TYPE_LOOKUPS = buildJobTypeLookups();

function normalizeJobTypeInput(raw: unknown, lookups: JobTypeLookups = DEFAULT_JOB_TYPE_LOOKUPS): { id: number; label: string } {
  const s = String(raw ?? '').trim();
  const n = Number(s);
  if (Number.isInteger(n) && n >= 0 && n <= 7) {
    return { id: n, label: lookups.labelById[n] ?? DEFAULT_JOB_TYPE_LOOKUPS.labelById[n] ?? '' };
  }

  const c = canon(s);
  const id = lookups.idByCanon[c] ?? 0;
  return { id, label: lookups.labelById[id] ?? DEFAULT_JOB_TYPE_LOOKUPS.labelById[id] ?? '' };
}

const jobTypeIdFromInput = (raw: unknown, lookups: JobTypeLookups = DEFAULT_JOB_TYPE_LOOKUPS) => normalizeJobTypeInput(raw, lookups).id;
const jobTypeLabelFromInput = (raw: unknown, lookups: JobTypeLookups = DEFAULT_JOB_TYPE_LOOKUPS) => normalizeJobTypeInput(raw, lookups).label;

const normalizeEmail = (e?: string | null) => (e ?? '').trim().toLowerCase();

const EXAMPLE_TEACHER_EMAILS = new Set([
  'example.teacher@mathswhizz.example',
  'example.teacher@example.com',
]);

const normalizeExampleValue = (value: unknown) => String(value ?? '').trim().toLowerCase();
const normalizeExamplePhone = (value: unknown) => String(value ?? '').replace(/\D/g, '');

const isValidEmail = (
  e?: string | null,
  { allowEmpty = false }: { allowEmpty?: boolean } = {},
) => {
  const s = normalizeEmail(e);
  if (!s) return !!allowEmpty;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

function buildExistingStudentUsernameCatalog(studentsList: unknown): ExistingStudentUsernameCatalog {
  const allUsernames = new Set<string>();
  const upnToUsername: Record<string, string> = {};
  const arr = Array.isArray(studentsList)
    ? studentsList
    : (Array.isArray((studentsList as { data?: unknown })?.data) ? (studentsList as { data: unknown[] }).data : []);

  arr.forEach((student) => {
    const studentRecord = student as {
      username?: unknown;
      userName?: unknown;
      login?: unknown;
      upn?: unknown;
      user?: { username?: unknown } | null;
    };
    const uname = usernameKey(String(studentRecord.username ?? studentRecord.userName ?? studentRecord.login ?? studentRecord.user?.username ?? ''));
    const upn = String(studentRecord.upn ?? '').trim();

    if (uname) {
      allUsernames.add(uname);
      if (upn) {
        upnToUsername[upn] = uname;
      }
    }
  });

  return { existingUsernames: allUsernames, upnToUsername };
}

function getWindowedRows<T extends { id: string }>(
  rows: T[],
  vStart: number,
  vEnd: number,
  editingId: string | null,
): WindowedRows<T> {
  const total = rows.length;
  let start = Math.min(vStart, total);
  let end = Math.min(Math.max(vEnd, start), total);

  if (editingId != null) {
    const idx = rows.findIndex((row) => row.id === editingId);
    if (idx >= 0) {
      const pad = OVERSCAN;
      const editStart = Math.max(0, Math.min(start, idx - pad));
      const editEnd = Math.max(end, Math.min(total, idx + pad + 1));

      if (editEnd - editStart <= MAX_WINDOWED_ROWS) {
        start = editStart;
        end = editEnd;
      }
    }
  }

  return {
    topPad: start * ROW_HEIGHT,
    bottomPad: Math.max((total - end) * ROW_HEIGHT, editingId ? 240 : 0),
    rows: rows.slice(start, end),
  };
}

function chunk<T>(arr: T[], size = 10): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const TagInput: React.FC<TagInputProps> = ({ value, onChange, allTags, placeholder, appendCommaOnPick = false, onCommit }) => {
  const [ open, setOpen ] = useState(false);
  const [ hoverIdx, setHoverIdx ] = useState(0);
  const [ dropUp, setDropUp ] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const parts = value.split(',').map((s) => s.trim());
  const query = value.endsWith(',') ? '' : (parts[parts.length - 1] ?? '');
  const prefix = parts.slice(0, -1).filter(Boolean);
  const searchTerm = query.trim().toLowerCase();
  const MAX_SUGGESTIONS = 8;

  const suggestions = useMemo(() => {
    if (!searchTerm) {
    // default list when empty: top N (stable + predictable)
      return allTags.slice(0, MAX_SUGGESTIONS);
    }
    const s = searchTerm.toLowerCase();
    const starts   = allTags.filter((t) => t.toLowerCase().startsWith(s));
    const contains = allTags.filter((t) => !t.toLowerCase().startsWith(s) && t.toLowerCase().includes(s));
    return [ ...starts, ...contains ].slice(0, MAX_SUGGESTIONS);
  }, [ searchTerm, allTags, MAX_SUGGESTIONS ]);

  useEffect(() => { setHoverIdx(0); }, [ open, suggestions.length ]);

  const commitPick  = (choice: string) => {
    let next = [ ...prefix, choice ].join(', ');
    if (appendCommaOnPick) {
      next = next.replace(/\s*$/, '') + ', ';
    }
    onChange(next);
    setOpen(false);
    setHoverIdx(0);
    requestAnimationFrame(() => onCommit?.());
  };

  useEffect(() => {
    if (!open) return;
    const fn = () => {
      if (!ref.current) return;
      const box = ref.current.getBoundingClientRect();
      const MENU_H = 200;
      const roomBelow = window.innerHeight - box.bottom;
      const roomAbove = box.top;
      setDropUp(roomBelow < MENU_H && roomAbove > roomBelow);
    };
    fn();
    window.addEventListener('resize', fn);
    window.addEventListener('scroll', fn, true);
    return () => {
      window.removeEventListener('resize', fn);
      window.removeEventListener('scroll', fn, true);
    };
  }, [ open ]);


  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
      setOpen(true);
      return;
    }

    if (!open)
      return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (suggestions[hoverIdx]) {
        e.preventDefault();
        commitPick(suggestions[hoverIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      onCommit?.();
    }
  };

  useEffect(() => {
    const onDocClickCapture = (ev: MouseEvent) => {
      if (!ref.current) return;
      const inside = ref.current.contains(ev.target as Node);
      if (!inside) {
        setOpen(false);
        onCommit?.();
      }
    };
    document.addEventListener('click', onDocClickCapture, true); // capture = true
    return () => document.removeEventListener('click', onDocClickCapture, true);
  }, [ onCommit ]);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const box = ref.current.getBoundingClientRect();

    // Approx menu height (matches your max-h-48 � 12rem � ~192px)
    const MENU_H = 200;

    const roomBelow = window.innerHeight - box.bottom;
    const roomAbove = box.top;

    // Flip up if there isn't enough room below but there is above
    setDropUp(roomBelow < MENU_H && roomAbove > roomBelow);
  }, [ open ]);

  return (
    <div className="relative" ref={ref}>
      <input
        className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => { setOpen(false); requestAnimationFrame(() => onCommit?.()); }}
      />
      {open && suggestions.length > 0 ? (
        <ul
          className="absolute z-20 max-h-48 w-full overflow-auto rounded border bg-white shadow"
          style={
            dropUp
              ? { bottom: '100%', marginTop: 0, marginBottom: '0.25rem' }
              : { top: '100%', marginTop: '0.25rem' }
          }
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s}-${i}`}
              className={`px-2 py-1 cursor-pointer ${i === hoverIdx ? 'bg-gray-100' : ''}`}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); commitPick(s); }}
            >
              {s}
            </li>
          ))}
        </ul>
      ) : null}

    </div>
  );
};

const StudentUploadPage: React.FC<StudentUploadPageProps> = ({
  initialTab = 'students',
  lockTab = false,
  onDirtyChange,
  autoOpenFilePicker = false,
  onAutoOpenFilePicker,
}) => {
  const { me } = useIdentity();
  const schoolId = (me as TeacherModel)?.getSchoolId();
  const currentTeacherEmail = normalizeEmail((me as TeacherModel)?.getEmail());
  const [ batchTotal, setBatchTotal ] = useState<{ total: number, done: number }>({ total: 0, done: 0 });
  const [ pendingUsernameSuggestions, setPendingUsernameSuggestions ] = useState<Record<string, string>>({});
  const [ progress, setProgress ] = useState<ProgressPhase>('idle');
  const [ activeTab ] = useState<UploadTabs>(initialTab);
  const currentTab = lockTab ? initialTab : activeTab;
  const [ students, setStudents ] = useState<Student[]>([]);
  const [ teachers, setTeachers ] = useState<Teacher[]>([]);
  const [ parents, setParents ] = useState<Parent[]>([]);
  const [ editingId, setEditingId ] = useState<string | null>(null);
  const [ studentDrafts, setStudentDrafts ] = useState<Record<string, Student>>({});
  const [ creatingTagName, setCreatingTagName ] = useState<string | null>(null);
  const [ creatingClassName, setCreatingClassName ] = useState<string | null>(null);
  const noop = () => undefined;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAutoOpenedFilePickerRef = useRef(false);
  const autoOpenAttemptedRef = useRef(false);
  const [ selectedFileName, setSelectedFileName ] = useState<string>('');
  const { translate } = useI18n();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [ initialStudentSortPending, setInitialStudentSortPending ] = useState(false);
  const [ vStart, setVStart ] = useState(0);
  const [ vEnd, setVEnd ] = useState(40);

  const existingStudentUsernamesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS, 'upload-existing-usernames' ],
    async () => {
      const studentsList = await repository.StudentRepository.rosterManagementListItemsByAction(
        RosterManagementStudentActionEnum.view,
      );

      return buildExistingStudentUsernameCatalog(studentsList);
    },
    {
      enabled: currentTab === 'students',
    },
  );

  const tagsQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
    () => repository.TagsRepository.getTags(),
    {
      enabled: currentTab === 'students',
      select: buildTagCatalog,
    },
  );

  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ],
    () => repository.NewClassRepository.getClasses(),
    {
      enabled: currentTab === 'students' || currentTab === 'teachers',
      select: buildClassCatalog,
    },
  );

  const yearGroupsQuery = useQuery(
    [ QUERIES_IDS.YEAR_GROUPS ],
    () => repository.YearGroupRepository.allBySchoolId(schoolId!),
    {
      enabled: currentTab === 'students',
      select: buildYearGroupCatalog,
    },
  );

  const tagsCatalog = tagsQuery.data ?? EMPTY_TAG_CATALOG;
  const classesCatalog = classesQuery.data ?? EMPTY_CLASS_CATALOG;
  const yearGroupsCatalog = yearGroupsQuery.data ?? EMPTY_YEAR_GROUP_CATALOG;
  const existingUsernames = existingStudentUsernamesQuery.data?.existingUsernames ?? EMPTY_EXISTING_STUDENT_USERNAMES;
  const upnToUsername = existingStudentUsernamesQuery.data?.upnToUsername ?? EMPTY_UPN_TO_USERNAME;
  const ygCodeToId = yearGroupsCatalog.codeToId;
  const ygCodeToName = yearGroupsCatalog.codeToName;
  const classIdByName = classesCatalog.idByName;
  const tagIdByAbbrev = tagsCatalog.idByAbbrev;
  const allClassNames = classesCatalog.names;
  const suggestedClassNames = useMemo(
    () => allClassNames.filter((name) => !HIDDEN_CLASS_SUGGESTION_NAMES.has(name.trim().toLowerCase())),
    [ allClassNames ],
  );
  const allTagNames = tagsCatalog.names;
  const ygList = yearGroupsCatalog.list;
  const submitting = progress === 'saving';
  const isBusy = progress !== 'idle' || !!creatingTagName || !!creatingClassName;
  // Locale-based DOB behaviour
  const localeId = (
    (me as unknown as { locale?: { id?: string | null } })?.locale?.id ??
  'en-gb'
  ).toLowerCase();
  const isMonthFirstDobLocale = localeId === 'en-us' || localeId === 'en-nz';
  const appliedUsernameUpnsRef = useRef<Set<string>>(new Set());

  const nullYearGroupCodes = useMemo(
    () =>
      new Set(
        ygList
          .filter((yg:any) => yg && (yg as any).yearGroupId == null)
          .map((yg) => String(yg.code).trim().toLowerCase()),
      ),
    [ ygList ],
  );
  const validYearGroupIds = useMemo(() => new Set(Object.values(ygCodeToId)), [ ygCodeToId ]);

  const isNullYearGroupValue = useCallback((raw?: string | null): boolean => {
    const s = String(raw ?? '').trim();
    if (!s) return false;
    const lower = s.toLowerCase();
    return nullYearGroupCodes.has(lower);
  }, [ nullYearGroupCodes ]);

  // Prefer translated text, but fall back to English if the key is missing.
  const tr = useCallback((key: string, fallback: string) => {
    const v = translate?.(key);
    return v && v !== key ? v : fallback;
  }, [ translate ]);

  const BULK_PAYLOAD_SHAPE: 'raw' | 'envelope' = 'raw';
  const TEACHERS_BULK_PAYLOAD: { mode: 'envelope'; key: 'staff' | 'teachers' | 'rows' } = {
    mode: 'envelope',
    key: 'staff',
  };
  const [ ui, uiDispatch ] = useReducer(uiReducer, {
    alert: { open: false, title: null, body: null },
    confirm: { open: false, title: '', body: null, opts: {} },
  } as UIState);
  const alertResolveRef = useRef<(() => void) | null>(null);

  const closeAlert = useCallback(() => {
    uiDispatch({ type: 'ALERT_CLOSE' });
    alertResolveRef.current?.();
    alertResolveRef.current = null;
  }, []);

  const showAlert = useCallback((body: React.ReactNode, title?: string) => {
    uiDispatch({ type: 'ALERT_OPEN', title: title ?? null, body });
  }, []);

  const showAlertAndWait = useCallback((body: React.ReactNode, title?: string): Promise<void> => {
    uiDispatch({ type: 'ALERT_OPEN', title: title ?? null, body });
    return new Promise<void>((resolve) => {
      alertResolveRef.current = resolve;
    });
  }, []);

  // is anything to apply?
  const hasUsernameSuggestions = useMemo(
    () => Object.keys(pendingUsernameSuggestions || {}).length > 0,
    [ pendingUsernameSuggestions ],
  );
  const sortedTeachers = useMemo(() => {
    return [ ...teachers ].sort((a, b) => Number(!!b.error) - Number(!!a.error));
  }, [ teachers ]);

  const sortedParents = useMemo(() => {
    return [ ...parents ].sort((a, b) => Number(!!b.error) - Number(!!a.error));
  }, [ parents ]);
  const activeRowCount =
    currentTab === 'students' ? students.length :
      currentTab === 'teachers' ? sortedTeachers.length :
        sortedParents.length;

  // apply all & clear
  const applyAllUsernameSuggestions = useCallback(() => {
    applyUsernameSuggestions(pendingUsernameSuggestions);
    setPendingUsernameSuggestions({});
  }, [ pendingUsernameSuggestions ]);

  const preflightEnsureDicts = async (): Promise<PreflightDicts> => {
    setProgress('preparing');
    try {
      const tagDict   = await ensureAllUsedTagsExistWithConfirm();
      const classDict = await ensureAllUsedClassesExistWithConfirm();
      return { tagDict, classDict };
    } finally {
      setProgress('idle');
    }
  };

  const preflightEnsureClassDict = async (): Promise<ClassPreflightDicts> => {
    setProgress('preparing');
    try {
      const classDict = await ensureAllUsedClassesExistWithConfirm();
      return { classDict };
    } finally {
      setProgress('idle');
    }
  };

  useEffect(() => {
    const anyModalOpen = ui.alert.open || ui.confirm.open || isBusy;

    const onKey = (e: KeyboardEvent) => {
      if (!anyModalOpen) return;

      // Do not intercept Enter/Escape while the user is typing inside form fields
      if (isTypingInInput(e)) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        if (ui.alert.open) {
          closeAlert();
        } else if (ui.confirm.open) {
          answerConfirm(false);
        }
      }

      if (ui.confirm.open && e.key === 'Enter') {
      // plain Enter = confirm (also works with Ctrl/Cmd automatically)
        e.preventDefault();
        answerConfirm(true);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ closeAlert, ui.alert.open, ui.confirm.open, isBusy ]);

  const commitStudentPatch = useCallback(
    (id: string, patch: Partial<Student>) => {
      setStudents((prev) => {
        const idx = prev.findIndex((student) => student.id === id);
        if (idx < 0) return prev;
        const next = prev.slice();
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    },
    [],
  );

  const handleStudentDraftChange = useCallback((id: string, draft: Student | null) => {
    setStudentDrafts((prev) => {
      if (draft) return { ...prev, [id]: draft };
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setStudentEditingRow = useCallback((id: string | null) => {
    const el = scrollRef.current;
    const keep = el?.scrollTop ?? 0;
    setEditingId(id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!el) return;
        el.scrollTo({ top: Math.max(0, keep - 120), behavior: 'auto' });
      });
    });
  }, []);

  function showSubscriptionErrorModal(
    showAlert: (body: React.ReactNode, title?: string) => void,
    data: any,
    who: 'students' | 'teachers' | 'parents',
  ) {
    const needed = Number(data?.context?.needed ?? data?.needed ?? NaN);
    const remaining = Number(data?.context?.remaining ?? data?.remaining ?? NaN);
    const alertKey = 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS';
    const shortfall = Number.isFinite(needed) && Number.isFinite(remaining)
      ? Math.max(0, needed - remaining)
      : undefined;

    const body = (
      <div className="space-y-3">
        <div className="text-sm">
          {translate(`${alertKey}.NOT_ENOUGH_SUBSCRIPTIONS_BODY`, { who })}
        </div>

        {Number.isFinite(needed) && Number.isFinite(remaining) ? (
          <div className="rounded-lg border p-3 text-sm">
            <div className="flex items-center justify-between">
              <span>{translate(`${alertKey}.REQUIRED_FOR_UPLOAD`)}</span>
              <b>{needed}</b>
            </div>
            <div className="flex items-center justify-between">
              <span>{translate(`${alertKey}.AVAILABLE_ON_PLAN`)}</span>
              <b>{remaining}</b>
            </div>
            <hr className="my-2" />
            <div className="flex items-center justify-between">
              <span>{translate(`${alertKey}.SHORTFALL`)}</span>
              <b>{shortfall}</b>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-700">
            {translate(`${alertKey}.NOT_ENOUGH_SUBSCRIPTIONS_FALLBACK`)}
          </div>
        )}

        <ul className="list-disc pl-5 text-sm text-gray-700">
          <li>{translate(`${alertKey}.REDUCE_UPLOAD_ROWS`, { who })}</li>
          <li>{translate(`${alertKey}.CONTACT_SUPPORT_TOP_UP`)}</li>
        </ul>
      </div>
    );

    showAlert(body, translate(`${alertKey}.NOT_ENOUGH_SUBSCRIPTIONS_TITLE`));
  }

  const unwrapBulkUploadResponse = (response: any): BulkUploadResponseSummary | null => {
    const candidates = [
      response,
      response?.data,
      response?.body,
      response?.response?.data,
      response?.data?.data,
      response?.data?.body,
      response?.data?.result,
      response?.data?.payload,
      response?.body?.data,
      response?.result,
      response?.payload,
    ];

    const data = candidates.find((candidate) =>
      candidate &&
      typeof candidate === 'object' &&
      (candidate.message || candidate.succeeded !== undefined || candidate.activation !== undefined),
    );

    return data ? data as BulkUploadResponseSummary : null;
  };

  const makeBulkUploadMessage = (summaries: BulkUploadResponseSummary[]): BulkUploadMessage | null => {
    const alertKey = 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS';
    const successTitle = translate(`${alertKey}.ACCOUNTS_CREATED_SUCCESSFULLY`);
    const accountsLine = (count: number) => translate(
      `${alertKey}.${count === 1 ? 'STUDENT_ACCOUNT_CREATED_OR_UPDATED_ONE' : 'STUDENT_ACCOUNT_CREATED_OR_UPDATED_MANY'}`,
      { count },
    );
    const activeLine = (count: number) => translate(
      `${alertKey}.${count === 1 ? 'STUDENT_ACTIVE_BASED_ON_SUBSCRIPTIONS_ONE' : 'STUDENT_ACTIVE_BASED_ON_SUBSCRIPTIONS_MANY'}`,
      { count },
    );
    const remainingLine = (count: number) => translate(
      `${alertKey}.${count === 1 ? 'ACTIVATE_REMAINING_ACCOUNT_ONE' : 'ACTIVATE_REMAINING_ACCOUNT_MANY'}`,
      { count },
    );
    const supportLine = translate(`${alertKey}.SUPPORT_CONTACT`);

    const normalizeBackendMessage = (message?: BulkUploadMessage): BulkUploadMessage | null => {
      if (!message) return null;

      const accountsCount = Number(message.accounts?.count ?? NaN);
      const activeCount = Number(message.active?.count ?? NaN);

      return {
        title: message.titleKey ? translate(message.titleKey) : (message.title ?? successTitle),
        accountsLine: Number.isFinite(accountsCount) ? accountsLine(accountsCount) : message.accountsLine,
        activeLine: Number.isFinite(activeCount) ? activeLine(activeCount) : message.activeLine,
        remainingLine: message.remaining ?? message.remainingLine ?? null,
        supportLine: message.support ?? message.supportLine ?? null,
      };
    };

    const usable = summaries.filter(Boolean);
    if (!usable.length) return null;

    if (usable.length === 1 && usable[0].message) {
      const single = usable[0];
      const succeeded = Number(single.succeeded ?? NaN);
      const activated = Number(single.activation?.activated ?? NaN);
      const inactive = Number(single.activation?.inactive ?? NaN);

      if (Number.isFinite(succeeded) && Number.isFinite(activated) && Number.isFinite(inactive)) {
        return {
          title: successTitle,
          accountsLine: accountsLine(succeeded),
          activeLine: activeLine(activated),
          remainingLine: inactive > 0 ? remainingLine(inactive) : null,
          supportLine: inactive > 0 ? supportLine : null,
        };
      }

      return normalizeBackendMessage(single.message);
    }

    const succeeded = usable.reduce((sum, item) => sum + Number(item.succeeded ?? 0), 0);
    const activated = usable.reduce((sum, item) => sum + Number(item.activation?.activated ?? 0), 0);
    const inactive = usable.reduce((sum, item) => sum + Number(item.activation?.inactive ?? 0), 0);

    return {
      title: successTitle,
      accountsLine: accountsLine(succeeded),
      activeLine: activeLine(activated),
      remainingLine: inactive > 0 ? remainingLine(inactive) : null,
      supportLine: inactive > 0 ? supportLine : null,
    };
  };

  const makeLocalBulkUploadFallbackMessage = (rows: StudentBulkRow[]): BulkUploadMessage => {
    const alertKey = 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS';
    const succeeded = rows.length;
    const active = rows.filter((row) => row.active).length;

    return {
      title: translate(`${alertKey}.ACCOUNTS_CREATED_SUCCESSFULLY`),
      accountsLine: translate(
        `${alertKey}.${succeeded === 1 ? 'STUDENT_ACCOUNT_CREATED_OR_UPDATED_ONE' : 'STUDENT_ACCOUNT_CREATED_OR_UPDATED_MANY'}`,
        { count: succeeded },
      ),
      activeLine: translate(
        `${alertKey}.${active === 1 ? 'STUDENT_ACTIVE_BASED_ON_SUBSCRIPTIONS_ONE' : 'STUDENT_ACTIVE_BASED_ON_SUBSCRIPTIONS_MANY'}`,
        { count: active },
      ),
      remainingLine: null,
      supportLine: null,
    };
  };

  const splitRemainingLine = (line: string) => {
    const commaIndex = line.indexOf(',');
    if (commaIndex < 0) return { lead: line, rest: '' };
    return {
      lead: line.slice(0, commaIndex + 1),
      rest: line.slice(commaIndex + 1),
    };
  };

  const showBulkUploadMessageModal = (
    showAlert: (body: React.ReactNode, title?: string) => void | Promise<void>,
    message: BulkUploadMessage,
  ) => {
    const remainingLine = message.remainingLine?.trim();
    const remainingParts = remainingLine ? splitRemainingLine(remainingLine) : null;

    return showAlert(
      <div className="space-y-4 text-base">
        <div className="space-y-3 rounded border border-gray-400 p-3">
          {message.accountsLine && <div>{message.accountsLine}</div>}
          {message.activeLine && <div>{message.activeLine}</div>}
        </div>

        {remainingParts && (
          <div className="space-y-1">
            <div>
              <span className="font-semibold">{remainingParts.lead}</span>
              {remainingParts.rest}
            </div>
            {message.supportLine && <div>{message.supportLine}</div>}
          </div>
        )}
      </div>,
      message.title || 'Accounts created successfully',
    );
  };

  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  function parseRetryAfterMs(h: any): number | null {
    const ra = h?.['retry-after'];
    if (!ra) return null;
    const s = Number(ra);
    if (Number.isFinite(s)) return Math.max(0, s * 1000);
    return null;
  }

  function isRetryable(e: any): { retry: boolean; waitMs?: number } {
    const status = e?.response?.status ?? e?.status;
    // Network/transport errors (no status) � retry
    if (!status) return { retry: true };

    // 429: honor Retry-After if present
    if (status === 429) {
      const ms = parseRetryAfterMs(e?.response?.headers);
      return { retry: true, waitMs: ms ?? 10_000 };
    }

    // Typical transient server issues
    if ([ 500, 502, 503, 504 ].includes(status)) return { retry: true };

    // Anything else: don't retry (409, 400, 422 etc.)
    return { retry: false };
  }

  async function uploadGroupWithRetry(
    group: StudentBulkRow[],
    attempt = 1,
    maxRetries = 4,
  ) {
    try {
      return await bulkUploadStudents(group);
    } catch (e: any) {
      const { retry, waitMs } = isRetryable(e);
      if (!retry || attempt >= maxRetries) throw e;

      // Exponential backoff with jitter
      const base = waitMs ?? 1000 * Math.pow(2, attempt - 1); // 1s,2s,4s,8s�
      const jitter = Math.floor(Math.random() * 400);         // +0�400ms
      await sleep(base + jitter);
      return uploadGroupWithRetry(group, attempt + 1, maxRetries);
    }
  }

  // helper to apply suggestions to grid + local username index
  function applyUsernameSuggestions(sugs: Record<string, string>) {
    if (!sugs || !Object.keys(sugs).length) return;

    const applied = appliedUsernameUpnsRef.current;

    setStudents((prev) =>
      prev.map((s) => {
        const upn = String(s.upn ?? '').trim();
        const suggested = sugs[upn];

        if (!suggested) return s;

        const nextUsername = String(suggested).trim();
        const curUsername = String(s.username ?? '').trim();

        // only mark as "applied" if it actually changed to something real
        if (nextUsername && nextUsername.toLowerCase() !== curUsername.toLowerCase()) {
          applied.add(upn);
        }

        return { ...s, username: nextUsername };
      }),
    );
  }
  const activeRowCountRef = useRef(0);

  const recalcWindow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const viewportH = el.clientHeight || 0;
    const total = activeRowCountRef.current;
    const rowsPerView = Math.max(1, Math.ceil(viewportH / ROW_HEIGHT));
    const scrollTop = el.scrollTop || 0;

    const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const end = Math.min(total, start + rowsPerView + 2 * OVERSCAN);

    setVStart(start);
    setVEnd(end);
  }, []);

  useEffect(() => {
    activeRowCountRef.current = activeRowCount;
    recalcWindow();
  }, [ activeRowCount, currentTab, recalcWindow ]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        recalcWindow();
      });
    };

    // initial calc
    schedule();

    const onScroll = schedule;
    el.addEventListener('scroll', onScroll);
    let ro: ResizeObserver | null = null;
    if (!editingId) {
      ro = new ResizeObserver(schedule);
      ro.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [ recalcWindow, editingId ]);

  const clearGridExceptAppliedUsernames = useCallback(() => {
    const applied = appliedUsernameUpnsRef.current;

    setStudents((prev) =>
      prev.filter((s) => applied.has(String(s.upn ?? '').trim())),
    );

    setEditingId(null);
    setPendingUsernameSuggestions({});
    requestAnimationFrame(() => recalcWindow());
  }, [ recalcWindow ]);

  const confirmResolveRef = useRef<(v: boolean) => void>();

  const askConfirm = useCallback(
    (body: React.ReactNode, opts: ConfirmOptions = {}): Promise<boolean> => {
      uiDispatch({ type: 'CONFIRM_OPEN', title: opts.title ?? '', body, opts });
      return new Promise<boolean>((resolve) => {
        confirmResolveRef.current = resolve;
      });
    },
    [],
  );

  const answerConfirm = (val: boolean) => {
    uiDispatch({ type: 'CONFIRM_CLOSE' });
    confirmResolveRef.current?.(val);
    confirmResolveRef.current = undefined;
  };

  const GENDER_OPTIONS = useMemo(
    () => [
      { value: '', label: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_NONE', '�') },
      { value: 'M', label: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_MALE', 'Male') },
      { value: 'F', label: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_FEMALE', 'Female') },
      { value: 'Other', label: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_OTHER', 'Other') },
    ],
    [ translate ],
  );
  const localizedGenderAliases = useMemo<LocalizedGenderAliases>(() => ({
    male: [
      String(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_MALE', 'Male')).trim().toLowerCase(),
      String(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE1.GENDER', 'Male')).trim().toLowerCase(),
      String(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BLANK_TEMPLATE_EXAMPLE.STUDENTS.GENDER', 'Male')).trim().toLowerCase(),
    ],
    female: [
      String(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER_FEMALE', 'Female')).trim().toLowerCase(),
      String(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.EXPORT_EXAMPLES.STUDENTS.EXAMPLE2.GENDER', 'Female')).trim().toLowerCase(),
    ],
  }), [ translate ]);
  const jobTypeLookups = useMemo<JobTypeLookups>(
    () => buildJobTypeLookups((key, fallback) => translate(key, fallback)),
    [ translate ],
  );

  const busyText = useMemo(() => {
    return creatingTagName ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BUSY.CREATING_TAG', { name: creatingTagName }) :
      creatingClassName ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BUSY.CREATING_CLASS', { name: creatingClassName }) :
        progress === 'preparing' ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BUSY.PREPARING') :
          progress === 'saving'
            ? `${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.BUSY.SAVING')}${batchTotal.total ? ` � ${batchTotal.done} / ${batchTotal.total}` : ''}`
            : '';
  }, [ creatingTagName, creatingClassName, progress, batchTotal.done, batchTotal.total, translate ]);

  const refreshTagsCache = async (): Promise<Record<string, number>> => {
    await queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ]);
    const tagsResponse = await queryClient.fetchQuery(
      [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
      () => repository.TagsRepository.getTags(),
    );
    return buildTagCatalog(tagsResponse).idByAbbrev;
  };

  const parseClassList = (s?: string | null) =>
    sanitizeClassList(s).split(',').map((t) => t.trim()).filter(Boolean);

  // TIAAN---return the mapping we should use for this submit
  const ensureAllUsedTagsExistWithConfirm = async (): Promise<Record<string, number>> => {
    //TIAAN--- collect all tags from the grid
    const used = new Set<string>();
    for (const s of students) parseTagList(s.tagName).forEach((n) => used.add(n));

    const missing = Array.from(used).filter((n) => !tagIdByAbbrev[n.toLowerCase()]);
    if (!missing.length) {
      //TIAAN--- nothing to create � return a copy of current map
      return { ...tagIdByAbbrev };
    }

    for (const name of missing) {
      if (name.length > 5) {
        showAlert(<>The tag <b>{name}</b> {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.TAG_TOO_LONG_BODY')}</>, translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.TAG_TOO_LONG_TITLE'));
        continue;
      }

      const proper = name.toUpperCase();

      const yes = await askConfirm(
        <>The tag <b>�{name}�</b> doesn�t exist. Create it now as <b>�{proper}�</b>?</>,
        { title: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE_TAG_Q_TITLE'), confirmText: translate('components.BUTTONS.CONFIRM2'), cancelText: translate('components.BUTTONS.CANCEL') },
      );
      if (!yes) continue;

      try {
        setCreatingTagName(proper);
        await repository.TagsRepository.addTag({ abbreviation: proper, description: proper, color: 1 });

        //  TIAAN---(optional) normalize what�s typed so UI shows the canonical uppercase
        setStudents((prev) => prev.map((student) => {
          const parts = student.tagName.split(',').map((t) => t.trim()).filter(Boolean);
          const changed = parts.some((p) => p.toLowerCase() === name.toLowerCase());
          if (!changed) return student;
          const next = parts.map((p) => (p.toLowerCase() === name.toLowerCase() ? proper : p)).join(', ');
          return { ...student, tagName: next };
        }));
      } catch (e: any) {
        const msg = e?.response?.data || e?.message || 'Unknown error';
        showAlert(<pre className="whitespace-pre-wrap text-sm">{String(msg)}</pre>, translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE_TAG_FAILED_TITLE'));
      } finally {
        setCreatingTagName(null);
      }
    }
    return refreshTagsCache(); // TIAAN---make sure id mapping is up-to-date
  };

  const ensureAllUsedClassesExistWithConfirm = async (): Promise<Record<string, number>> => {
    // Collect used classes from BOTH students + teachers (case-insensitive)
    const used = new Set<string>();
    const isAllToken = (n: string) => n.trim().toLowerCase() === 'all';

    for (const s of students) {
      parseClassList(s.className)
        .filter((n) => !isAllToken(n))
        .forEach((n) => used.add(n));
    }

    for (const t of teachers) {
      parseClassList(t.classes)
        .filter((n) => !isAllToken(n))
        .forEach((n) => used.add(n));

      parseClassList(t.emailClasses)
        .filter((n) => !isAllToken(n))
        .forEach((n) => used.add(n));
    }

    const missing = Array.from(used).filter((n) => !classIdByName[n.toLowerCase()]);
    if (missing.length === 0) return { ...classIdByName };

    // Local working copies (NO refetch)
    const nextDict: Record<string, number> = { ...classIdByName };
    const nextNamesSet = new Set<string>(allClassNames.map((n) => String(n).trim()).filter(Boolean));

    const getIdFromCreateResp = (resp: any): number | null => {
      // handle: { id: 123 } OR axios-like { data: { id: 123 } }
      const id =
        resp?.id ??
        resp?.data?.id ??
        resp?.response?.data?.id;

      const n = Number(id);
      return Number.isFinite(n) ? n : null;
    };

    for (const rawName of missing) {
      const name = rawName.trim();

      const yes = await askConfirm(
        translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE_CLASS_Q_BODY', { name }),
        {
          title: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE_CLASS_Q_TITLE'),
          confirmText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE'),
          cancelText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.SKIP'),
        },
      );
      if (!yes) continue;

      try {
        // IMPORTANT: kick off the request FIRST so it shows up immediately in DevTools
        const createPromise = (repository as any).NewClassRepository.create({ name } as any);

        // Optional busy text (can be expensive on huge tables)
        // If this still causes UI freezes, remove these two lines completely.
        setCreatingClassName(name);

        const resp = await createPromise;
        const newId = getIdFromCreateResp(resp);

        if (newId != null) {
          nextDict[name.toLowerCase()] = newId;
          nextNamesSet.add(name);
        }
      } catch (e: any) {
        const msg = e?.response?.data || e?.message || 'Unknown error';
        showAlert(
          <pre className="whitespace-pre-wrap text-sm">{String(msg)}</pre>,
          translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.CREATE_CLASS_FAILED_TITLE', 'Create class failed'),
        );
      } finally {
        setCreatingClassName(null);
      }
    }

    const nextNames = Array.from(nextNamesSet).sort((a, b) => a.localeCompare(b));
    queryClient.setQueryData<CatalogsState['classes']>(
      [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ],
      { names: nextNames, idByName: nextDict },
    );

    return nextDict;
  };

  const dirty =
      currentTab === 'students' ? students.length > 0 :
        currentTab === 'teachers' ? teachers.length > 0 :
          parents.length > 0;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [ dirty, onDirtyChange ]);

  const formatYearGroup = useCallback((raw?: string | null) => displayYearGroupName(raw), [ ygList, ygCodeToName ]);

  // "y 5", "year 5", "3 year", "6 y", "6y", "5year", "y7", "y 8", "Grade 5"
  const extractYearNumber = (raw?: string | null): number | null => {
    const s = String(raw ?? '').toLowerCase().trim();
    if (!s) return null;

    // Reception / Year R special case (optional � wire to real code if you have it)
    if (/\b(r|yr|rec|reception)\b/.test(s)) return 0; // treat as Year 0 / Reception

    const m =
    s.match(/(?:^|\b)(?:y|yr|year|grade)\s*(\d{1,2})(?:\b|$)/i) ||
    s.match(/(?:^|\b)(\d{1,2})\s*(?:y|yr|year|grade)(?:\b|$)/i) ||
    s.match(/(?:^|\b)y\s*(\d{1,2})(?:\b|$)/i) ||
    s.match(/(\d{1,2})/);

    if (!m) return null;
    const n = Number(m[1]);
    return n >= 0 && n <= 13 ? n : null;
  };

  // or '' if it cannot be matched to a backend group.
  const normalizeYearGroupToBackendCode = (raw?: string | null): string => {
    const s = String(raw ?? '').trim();
    if (!s) return '';

    // 1) Already a known backend code?
    const direct = ygCodeToId[s.toUpperCase()];
    if (Number.isFinite(direct)) return s.toUpperCase();

    if (/\bfoundation\b/i.test(s)) {
      const foundation = ygList.find((yg) =>
        /^f$/i.test(String(yg.code)) ||
      /\bfoundation\b/i.test(String(yg.name ?? '')),
      );

      if (foundation?.code) {
        return String(foundation.code).toUpperCase(); // e.g. "F"
      }
    }
    // 2) Try to pull a number and match the backend list
    const n = extractYearNumber(s);
    if (n == null) return '';

    // Reception / Year R (map to whatever your backend uses, e.g., "YR" or "Y0")
    if (n === 0) {
      const rec = ygList.find((yg) => /(^|\b)(r|rec|reception)\b/i.test(yg.code) || /(^|\b)(r|rec|reception)\b/i.test(yg.name));
      return rec ? String(rec.code).toUpperCase() : '';
    }

    // Prefer exact "Y<n>" code if present
    const preferred = `Y${n}`.toUpperCase();
    if (Number.isFinite(ygCodeToId[preferred])) return preferred;

    // Otherwise, look up any yg where code OR name contains that numeric grade
    const hit = ygList.find((yg) => {
      const codeNum = yg.code?.match(/(\d{1,2})/)?.[1];
      const nameNum = yg.name?.match(/(\d{1,2})/)?.[1];
      return (codeNum && Number(codeNum) === n) || (nameNum && Number(nameNum) === n);
    });

    return hit ? String(hit.code).toUpperCase() : '';
  };

  // Strict resolver for validation/payload: raw ? backend **ID** or null
  const resolveYearGroupIdStrict = (raw?: string | null): number | null => {
    const s = String(raw ?? '').trim();
    if (!s) return null;

    // 1) Direct hit: already a backend code (e.g., "Y10", "YR")
    const directId = ygCodeToId[s.toUpperCase()];
    if (Number.isFinite(directId)) return directId;

    // 2) Normalized code from your helper (e.g., "Year 10" -> "Y10")
    const norm = normalizeYearGroupToBackendCode(s); // returns a code string or ''
    if (norm) {
      const id = ygCodeToId[norm.toUpperCase()];
      if (Number.isFinite(id)) return id;
    }

    // 3) Extract a numeric grade and match by code or name (e.g., "Grade 11", "11")
    const n = extractYearNumber(s); // returns 0..13 or null
    if (n != null) {
    // Reception / Year R / Y0 special-case: map to whatever exists in your catalog
      if (n === 0) {
        const rec = ygList.find(
          (yg) =>
            /(^|\b)(r|rec|reception)\b/i.test(String(yg.code)) ||
          /(^|\b)(r|rec|reception)\b/i.test(String(yg.name)),
        );
        if (rec && Number.isFinite(rec.id)) return Number(rec.id);
      }

      // Prefer exact "Y<n>" code if present (e.g., "Y11")
      const preferredCode = `Y${n}`.toUpperCase();
      if (Number.isFinite(ygCodeToId[preferredCode])) return ygCodeToId[preferredCode];

      // Otherwise, scan catalog for any item whose code OR name contains that number
      const hit = ygList.find((yg) => {
        const codeNum = String(yg.code ?? '').match(/(\d{1,2})/)?.[1];
        const nameNum = String(yg.name ?? '').match(/(\d{1,2})/)?.[1];
        return (codeNum && Number(codeNum) === n) || (nameNum && Number(nameNum) === n);
      });
      if (hit && Number.isFinite(hit.id)) return Number(hit.id);
    }

    // 4) Nothing matched � signal invalid (null), never 0
    return null;
  };


  // Display helper: given whatever�s in the cell, show the backend **name** if we can
  const displayYearGroupName = (raw?: string | null): string => {
  // if it's already a known backend code, show the known name
    const code = String(raw ?? '').trim().toUpperCase();
    if (ygCodeToName[code]) return ygCodeToName[code];

    // otherwise try to normalize and then show name
    const norm = normalizeYearGroupToBackendCode(raw);
    return norm && ygCodeToName[norm] ? ygCodeToName[norm] : (raw ?? '');
  };


  //TIAAN--- picks the first non-empty value from possible CSV header
  const pick = (row: any, ...keys: string[]) => {
    for (const k of keys) {
      const v = row[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };
  const stripHtml = (s: string) => (s ?? '').replace(/<[^>]*>/g, '').trim();

  const normHeader = (s: string) =>
    stripHtml(s)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  // Turn { "????????????????????": "D123" } into { "????????????????????" (normalized): "D123" }
  const normalizeRowKeys = (row: any) => {
    const out: any = {};
    for (const [ k, v ] of Object.entries(row ?? {})) {
      out[normHeader(String(k))] = v;
    }
    return out;
  };

  const pickByHeaders = (rowNorm: any, ...headers: string[]) => {
    for (const h of headers) {
      const v = rowNorm[normHeader(h)];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const studentHeaders = (t: (k: string) => string) => {
    const T = (k: string) => stripHtml(t(k)); // IMPORTANT: removes <i>...</i> if any

    return {
      archive: [ 'Archive', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE') ],
      upn: [ 'UPN', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN') ],
      firstName: [ 'First Name', 'FirstName', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME') ],
      lastName: [ 'Last Name', 'LastName', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME') ],
      dob: [ 'Date Of Birth', 'DateOfBirth', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB') ],
      yearGroup: [ 'Year Group', 'YearGroup', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP') ],
      gender: [ 'Gender', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER') ],
      password: [ 'Password', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD') ],
      username: [ 'Username', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME') ],
      license: [ 'Activate Student', 'License', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE') ],
      className: [ 'Class', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS') ],
      tags: [ 'Tags', 'Tag', 'Tag 1', 'Tag1', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG') ],
      aboveBelow: [
        'Working Age',
        'Above/Below Assessment Delta',
        'Above Below Assessment Delta',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ABOVE_BELOW'),
      ],
      assessmentDelta: [
        'Start assessment below actual age (years)',
        'Assessment Age',
        'Assessment Delta',
        'Assess Delta',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA'),
      ],
    };
  };

  // TIAAN---Build guardians[] from the row, validating emails
  function collectGuardians(parent: Parent) {
    const guardians: Array<{ name: string; email: string }> = [];
    const errors: string[] = [];

    const pushIf = (name?: string, email?: string, label?: string) => {
      const n = (name ?? '').trim();
      const e = normalizeEmail(email ?? '');

      if (!n && !e) return;

      if (e && !isValidEmail(e)) {
        errors.push(`Invalid ${label} email: ${email}`);
        return;
      }

      guardians.push({ name: n, email: e });
    };

    pushIf((parent as any).g1Name, (parent as any).g1Email, 'guardian 1');
    pushIf((parent as any).g2Name, (parent as any).g2Email, 'guardian 2');
    pushIf((parent as any).g3Name, (parent as any).g3Email, 'guardian 3');

    return { guardians, errors };
  }


  const studentDuplicateCatalog = useMemo(() => {
    const upnCount: Record<string, number> = {};
    const usernameCount: Record<string, number> = {};

    students.forEach(({ upn, username }) => {
      upnCount[upn] = (upnCount[upn] || 0) + 1;

      const k = (username ?? '').trim().toLowerCase();
      if (!k) return;
      usernameCount[k] = (usernameCount[k] || 0) + 1;
    });

    const duplicateUPNs = Object.keys(upnCount).filter((upn) => upnCount[upn] > 1).sort();
    const duplicateUsernames = Object.keys(usernameCount).filter((k) => usernameCount[k] > 1).sort();

    return {
      duplicateUPNs,
      duplicateUPNsKey: JSON.stringify(duplicateUPNs),
      duplicateUsernames,
      duplicateUsernamesKey: JSON.stringify(duplicateUsernames),
    };
  }, [ students ]);

  const duplicateUPNs = useMemo(
    () => new Set(studentDuplicateCatalog.duplicateUPNs),
    [ studentDuplicateCatalog.duplicateUPNsKey ],
  );
  const duplicateUsernames = useMemo(
    () => new Set(studentDuplicateCatalog.duplicateUsernames),
    [ studentDuplicateCatalog.duplicateUsernamesKey ],
  );

  // Parse a local DOB string ("DD/MM/YYYY" or "MM/DD/YYYY" depending on locale)
  const parseDobPartsLocal = (s?: string | null): { d: number; mo: number; y: number } | null => {
    const v = (s ?? '').trim();
    if (!v) return null;

    // 1�2 digit day/month, 4 digit year, same separator
    const m = v.match(/^(\d{1,2})([-/\\])(\d{1,2})\2(\d{4})$/);
    if (!m) return null;

    const part1 = Number(m[1]);
    const part2 = Number(m[3]);
    const y = Number(m[4]);

    const d  = isMonthFirstDobLocale ? part2 : part1; // MM/DD vs DD/MM
    const mo = isMonthFirstDobLocale ? part1 : part2;

    if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y)) return null;
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;

    // Final sanity check using JS Date
    const dt = new Date(y, mo - 1, d);
    if (
      dt.getFullYear() !== y ||
      dt.getMonth() !== mo - 1 ||
      dt.getDate() !== d
    ) return null;

    return { d, mo, y };
  };

  const isValidDobDDMMYYYY = (s?: string | null): boolean => {
    return !!parseDobPartsLocal(s);
  };

  // "Y2" / "2" -> numeric ID that we consider valid if it exists in ygCodeToId
  const resolveYearGroupId = (raw?: string | null): number | null =>
    resolveYearGroupIdStrict(raw);

  // Parse local DOB ("DD/MM/YYYY" or "MM/DD/YYYY" depending on locale) into a UTC date, or null
  const parseDobToUTC = (s?: string | null): Date | null => {
    const parts = parseDobPartsLocal(s);
    if (!parts) return null;

    const { d, mo, y } = parts;
    const dt = new Date(Date.UTC(y, mo - 1, d));

    // ensure it's a real calendar date (e.g., not 31/02)
    if (
      dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
    ) return null;

    return dt;
  };

  const isFutureUTCDate = (dt: Date): boolean => {
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return dt.getTime() > todayUTC;
  };

  // "DD/MM/YYYY" or "DD-MM-YYYY" -> true if the date is in the future
  const isDobInFuture = (s?: string | null): boolean => {
    const dt = parseDobToUTC(s);
    return dt ? isFutureUTCDate(dt) : false;
  };

  // gender text -> 1/2/3, else null (invalid)
  const resolveGenderId = (raw?: string | null): number | null => {
    return mapGenderTextToId(raw);
  };

  const isShortPasswordWhenPresent = (s?: string | null) =>
    (s ?? '').trim().length > 0 && (s ?? '').trim().length < 5;

  const getStudentInlineErrors = useCallback((s: Student): string[] => {
    const errs: string[] = [];

    if (!s.upn?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.EMPTY_UPN', 'UPN is required'));
    } else if (duplicateUPNs.has(s.upn)) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DUPLICATE_UPN', 'Duplicate UPN in file'));
    }

    if (!s.firstName?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_FIRST_NAME', 'Missing first name'));
    }
    if (!s.lastName?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_LAST_NAME', 'Missing last name'));
    }

    if (!s.dateOfBirth?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_DOB', 'Missing date of birth'));
    } else if (!isValidDobDDMMYYYY(s.dateOfBirth)) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_DOB_FORMAT_DDMMYYYY', 'DOB must be DD/MM/YYYY'));
    } else if (isDobInFuture(s.dateOfBirth)) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DOB_IN_FUTURE', 'DOB cannot be in the future'));
    }

    // YEAR GROUP:
    if (!s.yearGroup?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_YEAR_GROUP', 'Missing year group'));
    } else {
      const ygId = resolveYearGroupId(s.yearGroup);
      if (ygList.length > 0) {
        const isNullOption = isNullYearGroupValue(s.yearGroup);
        const valid =
      isNullOption ||                      // ?? allow the �null� option
      (ygId != null && validYearGroupIds.has(ygId));

        if (!valid) {
          errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_YEAR_GROUP', 'Invalid year group'));
        }
      }
    }

    // GENDER:
    if (!s.gender?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_GENDER', 'Missing gender'));
    } else {
      const g = resolveGenderId(s.gender);
      if (g !== 1 && g !== 2 && g !== 3) {
        errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_GENDER', 'Invalid gender (use M/F/Other or 1/2/3)'));
      }
    }

    if (isShortPasswordWhenPresent(s.password)) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.PASSWORD_TOO_SHORT_5', 'Password must be at least 5 characters'));
    }

    if (!s.username?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_USERNAME', 'Missing username'));
    } else {
      const unameKey = s.username.trim().toLowerCase();
      if (duplicateUsernames.has(unameKey)) {
        errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DUPLICATE_USERNAME', 'Duplicate username in file'));
      }
      const upnKey = (s.upn ?? '').trim();
      if (upnKey) {
        const expectedUsername = upnToUsername[upnKey];
        if (expectedUsername && expectedUsername !== unameKey) {
          errs.push('This UPN is used by a different student.');
        }
      }
      const isNewInSchool = !!unameKey && !existingUsernames.has(unameKey);
      if (isNewInSchool && !(s.password ?? '').trim()) {
        errs.push('Password required for new student');
      }

    }


    if (!s.className?.trim()) {
      errs.push(tr('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_CLASS', 'Missing class'));
    }

    const assessmentYears = normalizeAssessmentDeltaYears(s.assessmentDelta);
    if (String(s.assessmentDelta ?? '').trim() && !assessmentYears) {
      errs.push('Assessment delta must be a whole number from 1 to 10');
    }

    return errs;
  }, [
    duplicateUPNs,
    duplicateUsernames,
    existingUsernames,
    isMonthFirstDobLocale,
    isNullYearGroupValue,
    tr,
    upnToUsername,
    validYearGroupIds,
    ygCodeToId,
    ygList,
  ]);


  const deleteStudent = useCallback((id: string) => {
    setEditingId((prev) => (prev === id ? null : prev));
    setStudents((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const parseYes = (v: unknown): boolean => {
    const s = String(v ?? '').trim().toLowerCase();
    return [ 'y', 'yes', 'true', '1', 't', 'x', '?', '?' ].includes(s);
  };

  function splitCsv(text: string): string[][] {
  // strip BOM, normalise newlines
    const s = text.replace(/^\uFEFF/, '');

    const rows: string[][] = [];
    let curCell = '';
    let curRow: string[] = [];
    let inQuotes = false;

    for (let i = 0; i < s.length; i++) {
      const ch = s[i];

      if (ch === '"') {
        const nxt = s[i + 1];
        if (inQuotes && nxt === '"') {
          curCell += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (ch === ',' && !inQuotes) {
        curRow.push(curCell);
        curCell = '';
        continue;
      }

      if ((ch === '\n' || ch === '\r') && !inQuotes) {

        curRow.push(curCell);
        curCell = '';
        if (curRow.length > 1 || (curRow.length === 1 && curRow[0].trim() !== '')) {
          rows.push(curRow);
        }
        curRow = [];


        if (ch === '\r' && s[i + 1] === '\n') i++;
        continue;
      }

      curCell += ch;
    }

    // flush last cell/row
    curRow.push(curCell);
    if (curRow.length > 1 || (curRow.length === 1 && curRow[0].trim() !== '')) {
      rows.push(curRow);
    }

    return rows;
  }
  // sort ONCE right after an upload populated the list
  useEffect(() => {
    if (!initialStudentSortPending) return;
    setStudents((prev) => {
      const next = [ ...prev ];
      // errors-first (descending by error count)
      next.sort(
        (a, b) => getStudentInlineErrors(b).length - getStudentInlineErrors(a).length,
      );
      return next;
    });
    setInitialStudentSortPending(false);

  }, [ initialStudentSortPending, duplicateUPNs, students.length ]);

  function parseCsv(text: string) {
    const grid = splitCsv(text);
    if (grid.length === 0) return { headers: [] as string[], rows: [] as any[] };

    const stripHeader = (s: unknown) =>
      String(s ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

    const headers = grid[0].map((h) => stripHeader(h));
    const rows = grid.slice(1).map((vals, i) => {
      const entry: any = { id: String(i + 1) };
      headers.forEach((h, idx) => { entry[h] = (vals[idx] ?? '').trim(); });
      return entry;
    });
    return { headers, rows };
  }
  //just for classes
  const sanitizeClassToken = (s: string) =>
    (s ?? '')
      .normalize('NFKC')
      .replace(/[^\p{L}\p{N}\s/\\.#@_=*&{}[\]:;`'"\]-]+/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  const sanitizeClassList = (s?: string | null) =>
    (s ?? '')
      .split(',')
      .map((t) => sanitizeClassToken(t))
      .filter(Boolean)
      .join(', ');

  // ISO "YYYY-MM-DD" ? true if the date is in the future
  const isIsoDobInFuture = (iso?: string | null): boolean => {
    const v = (iso ?? '').trim();
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return false;
    const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (
      dt.getUTCFullYear() !== y ||
      dt.getUTCMonth() !== mo - 1 ||
      dt.getUTCDate() !== d
    ) return false;
    return isFutureUTCDate(dt);
  };

  const reloadSoon = (ms = 800) =>
    window.setTimeout(() => window.location.reload(), ms);

  //TIAAN--- normalize "a, b,  c, " -> "a, b, c"
  const tidyDelimited = (s?: string | null) =>
    (s ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .join(', ');

  //TIAAN--- "ADHD, FSM" -> [ 19799, 34061 ]
  const toTagIds = (value?: string | null, dict: Record<string, number> = {}) =>
    (value ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => dict[t.toLowerCase()])
      .filter((id): id is number => Number.isFinite(id));

  //TIAAN--- "Content Class, Empty Class" -> [ 114570, 57736 ]
  const toClassIds = (value?: string | null, dict: Record<string, number> = {}) =>
    (value ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((name) => dict[name.toLowerCase()])
      .filter((id): id is number => Number.isFinite(id));

  const deleteTeacher = useCallback((id: string) => {
    setEditingId((prev) => (prev === id ? null : prev));
    setTeachers((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const deleteParent = useCallback((id: string) => {
    setEditingId((prev) => (prev === id ? null : prev));
    setParents((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const confirmDeleteRow = useCallback(async (id: string, onDelete: (id: string) => void) => {
    const ok = await askConfirm(
      <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DELETE_ROW_Q')}</p>,
      {
        title: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DELETE_ROW_TITLE'),
        destructive: true,
        confirmText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DELETE'),
      },
    );

    if (ok) onDelete(id);
  }, [ askConfirm, translate ]);

  const confirmDeleteTeacher = useCallback((id: string) => {
    return confirmDeleteRow(id, deleteTeacher);
  }, [ confirmDeleteRow, deleteTeacher ]);

  const confirmDeleteParent = useCallback((id: string) => {
    return confirmDeleteRow(id, deleteParent);
  }, [ confirmDeleteRow, deleteParent ]);

  const getParentInlineErrors = (p: Parent): string[] => {
    const errs: string[] = [];
    const upn = (p.upn ?? '').trim();
    const studentName = (p.studentName ?? '').trim();

    if (!upn) errs.push('UPN is required');
    if (!studentName) errs.push('Student Name is required');

    // Re-use your existing collector (it validates individual emails)
    const { guardians, errors: guardianErrs } = collectGuardians(p);
    errs.push(...guardianErrs);

    // Row-level: detect duplicates among g1/g2/g3 (non-empty only)
    const rowEmails = [ p.g1Email, p.g2Email, p.g3Email ]
      .map((e) => normalizeEmail(e))
      .filter(Boolean);

    const seenRow: Record<string, number> = {};
    rowEmails.forEach((e) => { seenRow[e] = (seenRow[e] || 0) + 1; });
    Object.entries(seenRow).forEach(([ email, count ]) => {
      if (count > 1) errs.push(`Duplicate guardian email in this row (${email})`);
    });



    // Require at least one complete guardian: name + valid email
    const hasCompleteGuardian = guardians.some((g) => {
      const hasName = !!(g.name ?? '').trim();
      const e = normalizeEmail(g.email);
      const hasValidEmail = !!e && isValidEmail(e);
      return hasName && hasValidEmail;
    });

    // If literally nothing provided anywhere (no names OR emails), keep your old message too
    const allBlank =
      !((p.g1Name ?? '').trim() || (p.g2Name ?? '').trim() || (p.g3Name ?? '').trim() ||
        normalizeEmail(p.g1Email) || normalizeEmail(p.g2Email) || normalizeEmail(p.g3Email));

    if (allBlank) {
      errs.push('At least one parent/guardian (name or email) is required');
    }

    // New, stronger requirement:
    if (!hasCompleteGuardian) {
      errs.push('At least one parent/guardian must have BOTH a name and a valid email');
    }

    return Array.from(new Set(errs)); // de-dup messages
  };

  const handleAddRow = useCallback(() => {
    const id = Date.now().toString();
    if (currentTab === 'students') {
      setStudents((prev) => [ ...prev, {
        id,
        upn: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        yearGroup: '',
        gender: '',
        password: makeDefaultPassword(),
        username: '',
        licenseActive: false,
        archive: false,
        className: '',
        tagName: '',
        aboveBelowAssessmentDelta: '',
        assessmentDelta: '',
      } ]);
    } else if (currentTab === 'teachers') {
      setTeachers((prev) => [ ...prev, {
        id,
        jobType: '',
        name: '',
        lastName: '',
        email: '',
        RecoveryEmail: '',
        classes: '',
        emailClasses: '',
        phone: '',
        admin: false,
        error: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_NAME'),
      } ]);
    } else if (currentTab === 'parents') {
      setParents((prev) => [ ...prev, {
        id, upn: '', studentName: '', g1Name: '', g1Email: '',
        g2Name: '', g2Email: '', g3Name: '', g3Email: '',
      } ]);
      setEditingId(id);
    }
  }, [ currentTab, makeDefaultPassword, translate ]);

  function parseAssessmentToEnum(
    raw?: string | number | null,
    aboveBelow: 'above' | 'below' = 'below',
  ): AssessmentStartAgeDelta | 0 {
    const s0 = String(raw ?? '').trim().toLowerCase();
    if (!s0) return 0;


    const numOrNull = (x: string) => (isFinite(Number(x)) ? Number(x) : null);

    // explicit "months" forms: "6 months", "6m", "m6", "0.5y" etc.
    const monthMatch =
      s0.match(/^(\d+)\s*m(?:onths?)?$/i) ||        // "6m" or "6 months"
      s0.match(/^m(?:onths?)?\s*(\d+)$/i);          // "m 6"
    const yearMatch =
      s0.match(/^(\d+(?:\.\d+)?)\s*y(?:ears?)?$/i) || // "1y" / "1 year" / "1.5y"
      s0.match(/^y(?:ears?)?\s*(\d+(?:\.\d+)?)$/i);

    // plain numbers like "0.5", "1", "2"
    const plainNum = numOrNull(s0);

    // Parse months/years into a float year value if possible
    let years: number | null = null;

    if (monthMatch) {
      const m = numOrNull(monthMatch[1]);
      if (m != null) years = m / 12;
    } else if (yearMatch) {
      years = numOrNull(yearMatch[1]);
    } else if (plainNum != null) {
      years = plainNum;
    }


    if (aboveBelow === 'below') {
    // Only supported: 6 months below, or 1..10 years below
      if (years != null) {
        if (years < 1) {
        // treat anything under a year as 6 months below (only supported "months" value)
          return AssessmentStartAgeDelta.MONTHS_BELOW_6; // -50
        }
        // clamp to 1..10 years
        const yrs = Math.max(1, Math.min(10, Math.round(years)));
        return (-100 * yrs) as AssessmentStartAgeDelta;  // -100 .. -1000
      }

      // textual fallbacks
      if (/6\s*m|month/.test(s0)) return AssessmentStartAgeDelta.MONTHS_BELOW_6;
      if (/^1/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_1;
      if (/^2/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_2;
      if (/^3/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_3;
      if (/^4/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_4;
      if (/^5/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_5;
      if (/^6/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_6;
      if (/^7/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_7;
      if (/^8/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_8;
      if (/^9/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_9;
      if (/^10/.test(s0)) return AssessmentStartAgeDelta.YEARS_BELOW_10;

      return 0;
    }

    if (aboveBelow === 'above') {
    // Only supported: 1 or 2 years above
      if (years != null) {
        if (years >= 1.5) return AssessmentStartAgeDelta.YEARS_ABOVE_2;
        if (years >= 0.5) return AssessmentStartAgeDelta.YEARS_ABOVE_1;
        return 0;
      }
      // textual fallbacks
      if (/^2/.test(s0)) return AssessmentStartAgeDelta.YEARS_ABOVE_2;
      if (/^1/.test(s0)) return AssessmentStartAgeDelta.YEARS_ABOVE_1;

      return 0;
    }

    return 0;
  }

  function normalizeAssessmentDeltaYears(raw?: string | number | null): '' | string {
    const s = String(raw ?? '').trim();
    if (!s) return '';

    const numeric = Number(s);
    if (Number.isFinite(numeric)) {
      if (numeric >= 1 && numeric <= 10 && Number.isInteger(numeric)) {
        return String(numeric);
      }
    }

    const parsed = Number(parseAssessmentToEnum(s, 'below'));
    if (parsed <= -100 && parsed >= -1000 && parsed % 100 === 0) {
      return String(Math.abs(parsed / 100));
    }

    return '';
  }

  function assessmentYearsToBelowEnum(raw?: string | number | null): AssessmentStartAgeDelta | 0 {
    const years = normalizeAssessmentDeltaYears(raw);
    return years
      ? ((-100 * Number(years)) as AssessmentStartAgeDelta)
      : AssessmentStartAgeDelta.MONTHS_BELOW_6;
  }

  const stripHeader = (s: unknown) =>
    String(s ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

  const T = {
    jobType: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE')),
    firstName: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME')),
    lastName: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME')),
    email: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL')),
    recoveryEmail: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL')),
    phone: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE')),
    classes: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES')),
    emailClasses: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES')),
    admin: stripHeader(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN')),
  };

  const isExampleTeacherRow = (row: Record<string, unknown>) => {
    const email = normalizeEmail(pick(row, T.email, 'Email Address', 'EmailAddress', 'Email') || '');
    if (EXAMPLE_TEACHER_EMAILS.has(email)) return true;

    const firstName = normalizeExampleValue(pick(row, T.firstName, 'First Name', 'FirstName', 'Name', 'TeachersName'));
    const lastName = normalizeExampleValue(pick(row, T.lastName, 'Last Name', 'LastName'));
    const phone = normalizeExamplePhone(pick(row, T.phone, 'Phone', 'Phone Number', 'Contact Number'));
    const classes = normalizeExampleValue(sanitizeClassList(pick(row, T.classes, 'Classes', 'ClassName', 'Class') || ''));

    return firstName === 'koa' &&
      lastName === 'khan' &&
      phone === '07900123456' &&
      classes === 'oak';
  };

  const parentHeaders = (t: (k: string) => string) => {
    const T = (k: string) => stripHeader(t(k));

    return {
      upn: [ 'UPN', 'upn', T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN') ],

      studentName: [
        'Student Name', 'StudentName', 'Pupil Name', 'Learner Name', 'Student',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME'),
      ],

      g1Name: [
        'Parent/Guardian 1 Name', 'Parent 1 Name', 'Guardian 1 Name',
        'Parent1 Name', 'Guardian1 Name', 'Parent 1', 'Guardian 1', 'Parents Name',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME'),
      ],
      g1Email: [
        'Email 1', 'Parent/Guardian 1 Email', 'Parent 1 Email', 'Guardian 1 Email',
        'Email Address 1', 'Parent1 Email', 'Guardian1 Email', 'Email Address',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL'),
      ],

      g2Name: [
        'Parent/Guardian 2 Name', 'Parent 2 Name', 'Guardian 2 Name',
        'Parent2 Name', 'Guardian2 Name', 'Parent 2', 'Guardian 2',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME'),
      ],
      g2Email: [
        'Email 2', 'Parent/Guardian 2 Email', 'Parent 2 Email', 'Guardian 2 Email',
        'Email Address 2', 'Parent2 Email', 'Guardian2 Email',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL'),
      ],

      g3Name: [
        'Parent/Guardian 3 Name', 'Parent 3 Name', 'Guardian 3 Name',
        'Parent3 Name', 'Guardian3 Name', 'Parent 3', 'Guardian 3',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME'),
      ],
      g3Email: [
        'Email 3', 'Parent/Guardian 3 Email', 'Parent 3 Email', 'Guardian 3 Email',
        'Email Address 3', 'Parent3 Email', 'Guardian3 Email',
        T('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL'),
      ],
    };
  };



  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { rows: parsed } = parseCsv(text);


      if (currentTab === 'students') {
        const taken = new Set<string>(existingUsernames);

        const H = studentHeaders(translate);

        const studentRows = parsed.filter((rawRow: any) => {
          const row = normalizeRowKeys(rawRow);
          return pickByHeaders(row, ...H.upn).trim().toUpperCase() !== 'EXAMPLE_STUDENT_DO_NOT_IMPORT';
        });

        const studentEntries = studentRows.map((rawRow: any, i: number) => {
          const row = normalizeRowKeys(rawRow);

          const first = pickByHeaders(row, ...H.firstName);
          const last  = pickByHeaders(row, ...H.lastName);

          const rawUsername = pickByHeaders(row, ...H.username);
          const usernameCell = rawUsername.trim().toLowerCase();

          // build: firstName + first letter of lastName
          const makeBaseUsername = () => {
            const f = (first || '').trim().toLowerCase();
            const l = (last || '').trim().toLowerCase();

            const firstLetterOfLast = l ? l[0] : '';

            // required rule
            let base = `${f}${firstLetterOfLast}`;

            // sanitize: remove spaces/symbols (keep a-z/0-9)
            base = base.replace(/[^a-z0-9]/g, '');

            // fallback if missing names
            if (!base) {
              const upn = pickByHeaders(row, ...H.upn).trim().toLowerCase().replace(/[^a-z0-9]/g, '');
              base = upn || `student${i + 1}`;
            }

            return base;
          };

          const rawPassword = pickByHeaders(row, ...H.password);
          const passwordCell = rawPassword.trim();


          const makeRandom2 = () => Math.floor(10 + Math.random() * 90);


          const upn = pickByHeaders(row, ...H.upn).trim();
          const existingForUpn = upnToUsername[upn] ?? '';
          const existingForUpnKey = existingForUpn.trim().toLowerCase();

          let finalPassword = passwordCell;


          const usernameFromSheet = pickByHeaders(row, ...H.username).trim().toLowerCase();
          const isExistingStudent =
  !!existingForUpnKey || (!!usernameFromSheet && existingUsernames.has(usernameFromSheet));

          if (!finalPassword && !isExistingStudent) {
            finalPassword = `whizz${makeRandom2()}`;
          } else if (!finalPassword && isExistingStudent) {
            finalPassword = '';
          }

          const makeUnique = (base: string) => {
            let u = base;
            let n = 1;
            while (!u || taken.has(u)) {
              u = `${base}${n++}`;
            }
            taken.add(u);
            return u;
          };

          let finalUsername = usernameCell;

          if (!finalUsername) {
            if (existingForUpnKey) {
              finalUsername = existingForUpnKey;
              taken.add(finalUsername); // keep duplicate tracking inside this file
            } else {
              finalUsername = makeUnique(makeBaseUsername());
            }
          }

          const rawTags = pickByHeaders(row, ...H.tags);

          return {
            id: String(i + 1),
            upn: pickByHeaders(row, ...H.upn),
            firstName: first,
            lastName: last,
            dateOfBirth: pickByHeaders(row, ...H.dob),
            yearGroup: (() => {
              const rawYG = pickByHeaders(row, ...H.yearGroup);
              const code = normalizeYearGroupToBackendCode(rawYG);
              return code || rawYG;
            })(),
            gender: normalizeGenderUI(pickByHeaders(row, ...H.gender), localizedGenderAliases),
            password: finalPassword,
            username: finalUsername,
            licenseActive: parseYes(pickByHeaders(row, ...H.license)),
            archive: parseYes(pickByHeaders(row, ...H.archive)),
            className: sanitizeClassList(pickByHeaders(row, ...H.className)),
            tagName: rawTags,
            aboveBelowAssessmentDelta: pickByHeaders(row, ...H.aboveBelow),
            assessmentDelta: normalizeAssessmentDeltaYears(pickByHeaders(row, ...H.assessmentDelta)),
          } as Student;
        });

        startTransitionSafe(() => {
          setStudents(studentEntries);
          setInitialStudentSortPending(true);
        });
      }
      else if (currentTab === 'teachers') {
        const teacherRows = parsed.filter((row: any) =>
          normalizeEmail(
            pick(row, T.email, 'Email Address', 'EmailAddress', 'Email') || '',
          ) !== 'example.teacher@example.com',
        );

        const teacherEntriesRaw = teacherRows.map((row: any, i: number) => ({
          id: String(i + 1),

          jobType: jobTypeLabelFromInput(
            pick(row, T.jobType, 'Job Type', 'JobType', 'jobType'),
            jobTypeLookups,
          ),

          name: pick(
            row,
            T.firstName,
            'First Name', 'FirstName',
            'Name', 'TeachersName',
          ) || '',

          lastName: pick(
            row,
            T.lastName,
            'Last Name', 'LastName',
          ) || '',

          email: normalizeEmail(
            pick(row, T.email, 'Email Address', 'EmailAddress', 'Email') || '',
          ),

          RecoveryEmail: normalizeEmail(
            pick(row, T.recoveryEmail, 'Recovery Email Address', 'RecoveryEmail', 'Recovery Email') || '',
          ),

          classes: sanitizeClassList(
            pick(row, T.classes, 'Classes', 'ClassName', 'Class') || '',
          ),

          emailClasses: sanitizeClassList(
            pick(
              row,
              T.emailClasses,
              'Weekly Class Email Updates',
              'Email Classes',
              'EmailClasses',
              'Email Permissions',
              'Email Permission Classes',
            ) || '',
          ),

          phone: pick(
            row,
            T.phone,
            'Phone', 'Phone Number', 'PhoneNumber', 'Mobile',
          ) || '',

          admin: parseYes(
            pick(
              row,
              T.admin,
              'Admin', 'Admin?', 'Is Admin', 'IsAdmin', 'Administrator',
              'Activate', 'Activate Student',
            ),
          ),
        }));

        const nameMap: Record<string, number> = {};
        const emailMap: Record<string, number> = {};
        teacherEntriesRaw.forEach((t) => {
          if (t.name) nameMap[t.name.toLowerCase()] = (nameMap[t.name.toLowerCase()] || 0) + 1;
          if (t.email) emailMap[t.email.toLowerCase()] = (emailMap[t.email.toLowerCase()] || 0) + 1;
        });

        const teacherEntries = teacherEntriesRaw.map((t) => {
          const nameKey = (t.name || '').toLowerCase();
          const email = normalizeEmail(t.email);
          const recovery = normalizeEmail(t.RecoveryEmail);
          const isCurrentTeacher = !!currentTeacherEmail && email === currentTeacherEmail;
          const hasEmail = !!email;
          const hasRecovery = !!recovery;
          const err =
            !nameKey
              ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_NAME')
              : (!hasEmail && !hasRecovery)
                ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_EMAIL')
                : (hasEmail && !isValidEmail(email))
                  ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_EMAIL')
                  : (hasRecovery && !isValidEmail(recovery, { allowEmpty: true }))
                    ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_RECOVERY_EMAIL')
                    : (hasEmail && (emailMap[email] || 0) > 1)
                      ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DUPLICATE_EMAIL')
                      : '';

          return { ...t, email, RecoveryEmail: recovery, admin: isCurrentTeacher ? true : t.admin, error: err };
        });
        setTeachers(teacherEntries);

      } else if (currentTab === 'parents') {
        const P = parentHeaders(translate);

        const parentRows = parsed.filter((row: any) =>
          String(pick(row, ...P.upn) ?? '').trim().toUpperCase() !== 'EXAMPLE_PARENT_DO_NOT_IMPORT',
        );

        const parentEntries = parentRows.map((row: any, i: number) => ({
          id: String(i + 1),

          upn: pick(row, ...P.upn),

          studentName: pick(row, ...P.studentName),

          g1Name: pick(row, ...P.g1Name),
          g1Email: normalizeEmail(pick(row, ...P.g1Email)),

          g2Name: pick(row, ...P.g2Name),
          g2Email: normalizeEmail(pick(row, ...P.g2Email)),

          g3Name: pick(row, ...P.g3Name),
          g3Email: normalizeEmail(pick(row, ...P.g3Email)),
        }));

        setParents(parentEntries);
      }
    };
    reader.readAsText(file);
  };

  const onPickFile = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSelectedFileName(f.name);
    handleFileUpload(e);
    e.target.value = '';
  }, [ handleFileUpload ]);

  useEffect(() => {
    if (!autoOpenFilePicker) {
      autoOpenAttemptedRef.current = false;
      return;
    }
    if (autoOpenAttemptedRef.current) return;
    autoOpenAttemptedRef.current = true;

    const frame = window.requestAnimationFrame(() => {
      fileInputRef.current?.click();
      onAutoOpenFilePicker?.();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [ autoOpenFilePicker, onAutoOpenFilePicker ]);

  const handleTeacherEditChange = useCallback((id: string, key: keyof Teacher, value: string) => {
    setTeachers((prev) => {
      const updated = prev.map((t) => {
        if (t.id !== id) return t;

        const v =
          key === 'email' || key === 'RecoveryEmail'
            ? normalizeEmail(value)
            : value;

        const updatedTeacher = { ...t, [key]: v };

        const name = updatedTeacher.name?.trim().toLowerCase();
        const email = normalizeEmail(updatedTeacher.email);
        const recovery = normalizeEmail(updatedTeacher.RecoveryEmail);
        const isCurrentTeacher = !!currentTeacherEmail && email === currentTeacherEmail;
        const hasEmail = !!email;
        const hasRecovery = !!recovery;
        const duplicateEmail = hasEmail && prev.some(
          (otherTeacher) => otherTeacher.id !== id && normalizeEmail(otherTeacher.email) === email,
        );

        let error = '';
        if (!name) {
          error = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_NAME');
        } else if (!hasEmail && !hasRecovery) {
          error = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.MISSING_EMAIL');
        } else if (hasEmail && !isValidEmail(email)) {
          error = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_EMAIL');
        } else if (hasRecovery && !isValidEmail(recovery, { allowEmpty: true })) {
          error = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.INVALID_RECOVERY_EMAIL');
        } else if (duplicateEmail) {
          error = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DUPLICATE_EMAIL');
        }

        return { ...updatedTeacher, admin: isCurrentTeacher ? true : updatedTeacher.admin, error };
      });

      return updated;
    });
  }, [ currentTeacherEmail, translate ]);

  const handleParentEditChange = useCallback((id: string, key: keyof Parent, value: string) => {
    setParents((prev) =>
      prev.map((parent) => (parent.id === id ? { ...parent, [key]: value } : parent)),
    );
  }, []);

  const handleSubmit = async () => {
    if (currentTab === 'students') {
      const dicts = await preflightEnsureDicts();
      await submitStudents(dicts);
    } else if (currentTab === 'teachers') {
      const dicts = await preflightEnsureClassDict();
      await submitTeachers(dicts);
    } else if (currentTab === 'parents') {
      await submitParents();
    }
  };

  const handleTeacherToggleAdmin = useCallback((id: string, checked: boolean) => {
    setTeachers((prev) => prev.map((t) => t.id === id ? { ...t, admin: checked } : t));
  }, []);

  // TIAAN---"M"/"Male" ---------> 1, "F"/"Female" -> 2,
  const mapGenderTextToId = (genderText?: string | null): number | null => {
    const s = (genderText ?? '').trim().toLowerCase();
    if (!s) return null;

    // numeric already?
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      if (n === 1 || n === 2 || n === 3) return n;
      return null;
    }

    if ([ 'm', 'male', 'boy', ...(localizedGenderAliases.male ?? []) ].includes(s)) return 1;
    if ([ 'f', 'female', 'girl', ...(localizedGenderAliases.female ?? []) ].includes(s)) return 2;
    if ([ 'o', 'other', 'non-binary', 'nonbinary', 'nb', 'x' ].includes(s)) return 3;

    return null;
  };

  // TIAAN --- Local DOB ("DD/MM/YYYY" or "MM/DD/YYYY") ---> "YYYY-MM-DD" for backend
  const toIsoDate = (dateText?: string | null): string | null => {
    if (!dateText) return null;
    const parts = parseDobPartsLocal(dateText);
    if (!parts) {

      return dateText.trim();
    }

    const { d, mo, y } = parts;
    const yyyy = String(y);
    const mm   = String(mo).padStart(2, '0');
    const dd   = String(d).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  };

  const buildStudentBulkRow = (
    student: Student,
    tagDict: Record<string, number>,
    classDict: Record<string, number>,
  ): StudentBulkRow => {
    const isNullYg = isNullYearGroupValue(student.yearGroup);
    const resolvedYgId = resolveYearGroupId(student.yearGroup);

    return {
      upn: (student.upn ?? '').trim(),
      firstName: (student.firstName ?? '').trim(),
      lastName: (student.lastName ?? '').trim(),
      dob: toIsoDate(student.dateOfBirth) ?? '',
      yearGroupId: isNullYg ? null : (resolvedYgId ?? 0),
      gender: mapGenderTextToId(student.gender) ?? 0,
      password: (student.password ?? '').trim(),
      username: (student.username ?? '').trim(),
      active: !!student.licenseActive,
      archive: !!student.archive,
      classesIds: toClassIds(tidyDelimited(student.className), classDict),
      tagsIds: toTagIds(tidyDelimited(student.tagName), tagDict),
      aboveBelowAssessmentDelta: -1,
      assessmentDelta: assessmentYearsToBelowEnum(student.assessmentDelta),
    };
  };

  const buildTeacherBulkRow = (
    teacher: Teacher,
    classDict: Record<string, number>,
  ): TeacherBulkRow => {
    const rawClasses = teacher.classes ?? '';

    // Look at tokens in the CSV cell: e.g. "all", "All", "ALL"
    const tokens = rawClasses
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const wantsAllClasses = tokens.includes('all');

    const classIds = wantsAllClasses
    // special case: "all" ? assign *every* class id we know about
      ? Object.values(classDict).filter((id): id is number => Number.isFinite(id))
    // normal case: behave exactly as before
      : toClassIds(rawClasses, classDict);

    const rawEmailClasses = teacher.emailClasses ?? '';

    const emailTokens = rawEmailClasses
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const wantsAllEmailClasses = emailTokens.includes('all');

    const emailClassIds = wantsAllEmailClasses
      ? Object.values(classDict).filter((id): id is number => Number.isFinite(id))
      : toClassIds(rawEmailClasses, classDict);

    return {
      firstName: (teacher.name ?? '').trim(),
      lastName: (teacher.lastName ?? '').trim(),
      jobType: jobTypeIdFromInput((teacher as any).jobType, jobTypeLookups),
      jobTitle: null,
      email: (teacher.email ?? '').trim().toLowerCase() || null,
      recoveryEmail: (teacher.RecoveryEmail ?? '').trim() || null,
      password: ((teacher as any).password ?? '').toString().trim() || null,
      username: ((teacher as any).username ?? '').toString().trim() || null,
      phone: (teacher.phone ?? '').toString().trim(),
      isAdmin: !!teacher.admin,
      cleverId: null,
      countryId: Number.isFinite(Number((teacher as any).countryId))
        ? Number((teacher as any).countryId)
        : null,
      ssoAccessId: null,
      ssoClientId: null,
      classIds,
      emailClassIds,
    };
  };


  const validateStudentBulk = (student: StudentBulkRow): string | null => {
    if (!student.firstName) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.FIRSTNAME_REQUIRED');
    if (!student.lastName) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.LASTNAME_REQUIRED');
    if (!student.dob) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DOB_REQUIRED');
    if (isIsoDobInFuture(student.dob)) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.DOB_IN_FUTURE');
    if (!student.upn) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.UPN_REQUIRED');

    if (student.yearGroupId !== null) {
    // If we *failed* to resolve, buildStudentBulkRow sets yearGroupId to 0.
    // Treat only positive integers as valid and let the server be the final judge.
      if (
        !Number.isInteger(student.yearGroupId) ||
      student.yearGroupId <= 0
      ) {
        return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.YEAR_GROUP_REQUIRED');
      }
    }

    if (![ 1, 2, 3 ].includes(student.gender)) {
      return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.GENDER_IS_INVALID');
    }

    if (!student.classesIds.length) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.AT_LEAST_ONE_CLASS_REQUIRED');

    const unameKey = (student.username ?? '').trim().toLowerCase();
    const isNewInSchool = !!unameKey && !existingUsernames.has(unameKey);
    if (isNewInSchool && !(student.password ?? '').trim()) {
      return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.PASSWORD_REQUIRED_FOR_NEW_STUDENT');
    }

    return null;
  };

  const validateTeacherBulkRow = (t: TeacherBulkRow): string | null => {
    if (!t.firstName) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.FIRSTNAME_REQUIRED');
    if (!t.lastName) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.LASTNAME_REQUIRED');
    const hasEmail = !!t.email;
    const hasRecovery = !!t.recoveryEmail;

    if (!hasEmail && !hasRecovery) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.EMAIL_OR_RECOVERY_EMAIL_REQUIRED');
    if (hasEmail && !isValidEmail(t.email)) return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.EMAIL_INVALID_FORMAT');
    if (hasRecovery && !isValidEmail(t.recoveryEmail, { allowEmpty: true })) {
      return translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ERRORS.RECOVERY_EMAIL_INVALID_FORMAT');
    }
    return null;
  };

  async function bulkUploadStudents(rows: StudentBulkRow[]) {
    const data = BULK_PAYLOAD_SHAPE === 'envelope'
      ? { rows }
      : { rows };

    const response = await client.http()({
      method: 'POST',
      url: '/students/bulk-upload',
      data,
    });

    return response.data;
  }

  async function bulkUploadTeachers(staff: TeacherBulkRow[]) {
    const staffRepo: any = (repository as any).StaffRepository;
    if (!staffRepo) throw new Error('StaffRepository not available');

    // Choose the method that exists; most SDKs expose one of these
    const fn =
    (typeof staffRepo.BulkUploadStaff === 'function' && staffRepo.BulkUploadStaff) ||
    (typeof staffRepo.BulkUploadTeachers === 'function' && staffRepo.BulkUploadTeachers) ||
    (typeof staffRepo.bulkUpload === 'function' && staffRepo.bulkUpload);

    if (!fn) throw new Error('No bulk staff upload method found on StaffRepository');

    // Envelope only � this is what your server requires.
    const body: Record<string, unknown> = {};
    body[TEACHERS_BULK_PAYLOAD.key] = staff;

    // Single attempt (prevents �duplicate row 1� issues)
    return await fn.call(staffRepo, body as any);
  }

  const parseTagList = (s?: string | null) =>
    (s ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  // TIAAN--- submit all students, with detailed errors ---
  const submitStudents = async ({ tagDict, classDict }: PreflightDicts) => {
    try {
      setProgress('saving');

      const preErrors: string[] = [];
      const payloadRows: StudentBulkRow[] = [];


      for (let i = 0; i < students.length; i++) {
        const s = students[i];

        // Skip rows that currently show any inline errors in the Error column
        const inlineErrs = getStudentInlineErrors(s);
        if (inlineErrs.length) {
          preErrors.push(`Row ${i + 1}: ${inlineErrs.join('; ')}`);
          continue;
        }

        const bulkRow = buildStudentBulkRow(s, tagDict, classDict);
        const validationError = validateStudentBulk(bulkRow);
        if (validationError) {
          preErrors.push(`Row ${i + 1}: ${validationError}`);
          continue;
        }

        payloadRows.push(bulkRow);
      }

      if (!payloadRows.length) {
        const shown = preErrors.slice(0, 20);
        const tail = preErrors.length > 20 ? `\n�and ${preErrors.length - 20} more error(s)` : '';
        showAlert(
          <div>
            <div className="mb-2">
              {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NO_ROWS_BODY_PREFIX')}
            </div>
            {shown.length > 0 && (
              <pre className="whitespace-pre-wrap text-sm">
                {shown.join('\n')}{tail}
              </pre>
            )}
          </div>,
          translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NO_ROWS_TITLE'),
        );
        return;
      }
      // 2) Upload in batches with retry
      const groups = chunk(payloadRows, 15);
      setBatchTotal({ total: payloadRows.length, done: 0 });

      const serverErrors: string[] = [];
      const uploadSummaries: BulkUploadResponseSummary[] = [];
      let processed = 0;

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        try {
          const response = await uploadGroupWithRetry(group);
          const summary = unwrapBulkUploadResponse(response);
          if (summary) uploadSummaries.push(summary);
        } catch (e: any) {
          const status = e?.response?.status ?? e?.status ?? 'ERR';
          const data   = e?.response?.data ?? e?.data;
          const msg    = typeof data === 'string' ? data : JSON.stringify(data ?? {}, null, 2);

          // If there�s a real subscription shortfall--stop--.
          if (status === 409 && /Not enough subscriptions/i.test(String(data?.error || data?.message))) {
            showSubscriptionErrorModal(showAlert, data, 'students');
            // Do not proceed further � batches after this will also fail.
            break;
          }

          // Already-exists block
          if (status === 409 && /Student already exists/i.test(String(data?.error || data?.message))) {
            const raw = data?.context?.duplicates ?? data?.context?.conflicts ?? data?.duplicates ?? data?.conflicts ?? data?.context?.rows ?? [];
            const listed: string[] = Array.isArray(raw)
              ? raw.map((r: any) => {
                const fn  = r?.firstName ?? r?.first_name ?? r?.childFirstName ?? '';
                const ln  = r?.lastName  ?? r?.last_name  ?? r?.childLastName  ?? '';
                const dob = r?.dateOfBirth ?? r?.dob ?? '';
                const upn = r?.upn ?? r?.UPN ?? '';
                const name = [ fn, ln ].filter(Boolean).join(' ');
                return `${name || '(no name)'} � ${dob || 'DOB ?'}${upn ? ` (UPN ${upn})` : ''}`;
              })
              : [];
            if (listed.length) {
              serverErrors.push(...listed.map((line) => `Already exists: ${line}`));
            } else {
              serverErrors.push('At least one student already exists (server didn�t specify rows).');
            }
          // IMPORTANT: continue with the remaining batches
          }
          // Username taken summary (server returns detailed results)
          else if (status === 409 && data?.results && Array.isArray(data.results)) {
            const failed = data.results.filter((r: any) => r && r.ok === false);

            // Build a map UPN -> suggestedUsername
            const sugs: Record<string, string> = {};
            failed.forEach((r: any) => {
              if (r.error === 'USERNAME_TAKEN' && r?.suggestion?.suggested) {
                const upn  = String(r.upn ?? '').trim();
                const orig = String(r.username ?? r?.suggestion?.input ?? '').trim();
                const sugg = String(r.suggestion.suggested).trim();

                if (upn && sugg) sugs[upn] = sugg;
                serverErrors.push(`${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN')} ${upn || '?'}: "${orig || '(username)'}" ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.IS_TAKEN')} "${sugg}"`);
              } else {
                const upn = String(r?.upn ?? '').trim();
                serverErrors.push(`UPN ${upn || '?'}: ${String(r?.error ?? 'Unknown error')}`);
              }
            });

            setPendingUsernameSuggestions((prev) => ({ ...prev, ...sugs }));
          // keep going
          }
          // Generic batch failure � collect and continue
          else {
            serverErrors.push(`Batch ${gi + 1}/${groups.length} failed [${status}]: ${msg}`);
          }
        } finally {
          processed += group.length;
          setBatchTotal((prev) => ({ ...prev, done: prev.done + group.length }));
        }
      }

      if (preErrors.length || serverErrors.length) {
        const lines = [
          ...(preErrors.length ? [ '� Pre-validation �', ...preErrors ] : []),
          ...(serverErrors.length ? [ '� Server �', ...serverErrors ] : []),
        ];
        showAlert(
          <div className="space-y-2">
            <div className="font-medium">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE')}</div>
            <div>? {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.APPROX')} {Math.max(0, processed - serverErrors.length - preErrors.length)} {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.SUCCEEDED')}</div>
            <div>? {preErrors.length + serverErrors.length} {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NEEDS_ATTENTION')}</div>
            <pre className="whitespace-pre-wrap text-sm mt-2">
              {lines.slice(0, 200).join('\n')}
              {lines.length > 200 ? `\n�and ${lines.length - 200} more` : ''}
            </pre>
          </div>,
          translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE'),
        );
        return;
      }

      if (processed < payloadRows.length) {
        // This happens only on a hard stop (e.g., subscriptions). We�ve already shown that modal.
        return;
      }

      // All batches completed with no errors
      const uploadMessage = makeBulkUploadMessage(uploadSummaries) ?? makeLocalBulkUploadFallbackMessage(payloadRows);
      await showBulkUploadMessageModal(showAlertAndWait, uploadMessage);
      reloadSoon();

    } catch (e: any) {
      const data = e?.response?.data ?? e?.data;
      showAlert(
        <>
          <div className="mb-2">Bulk upload failed.</div>
          <pre className="whitespace-pre-wrap text-sm mt-2">
            {JSON.stringify(data ?? {}, null, 2)}
          </pre>
        </>,
        'Error',
      );
    } finally {
      setProgress('idle');
      setBatchTotal({ total: 0, done: 0 });
    }
  };

  const submitTeachers = async ({ classDict }: ClassPreflightDicts) => {
    setProgress('saving');


    try {
      const preErrors: string[] = [];
      const staff: TeacherBulkRow[] = [];

      for (let i = 0; i < teachers.length; i++) {
        const t = teachers[i];
        const row = buildTeacherBulkRow(t, classDict);
        const err = validateTeacherBulkRow(row);
        if (err) { preErrors.push(`Row ${i + 1}: ${err}`); continue; }
        staff.push(row);
      }

      if (!staff.length) {
        showAlert(
          <pre className="whitespace-pre-wrap">{`Nothing to submit.\n\n${preErrors.join('\n')}`}</pre>,
          translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NO_ROWS_TITLE'),
        );
        return;
      }

      const groups = chunk(staff, 10);
      setBatchTotal({ total: staff.length, done: 0 });

      const serverErrors: string[] = [];

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        try {
          await bulkUploadTeachers(group);
        } catch (e: any) {
          const data   = e?.response?.data;
          // collect per-batch error text (server shapes can vary)
          serverErrors.push(`Batch ${gi + 1}/${groups.length} failed: ${JSON.stringify(data ?? {}, null, 2)}`);
        } finally {
          setBatchTotal((prev) => ({ ...prev, done: prev.done + group.length }));
        }
      }

      if (preErrors.length || serverErrors.length) {
        const header = `${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE')}:\n  ? ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.APPROX')} ${Math.max(0, batchTotal.total - (preErrors.length + serverErrors.length))} ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.SUCCEEDED')}\n  ? ${preErrors.length + serverErrors.length} ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NEEDS_ATTENTION')}\n\nDetails:\n`;
        const shown  = [ ...preErrors, ...serverErrors ].slice(0, 200);
        const tail   = (preErrors.length + serverErrors.length) > 200 ? `\n�and ${(preErrors.length + serverErrors.length) - 200} more` : '';
        showAlert(<pre className="whitespace-pre-wrap">{header + shown.join('\n') + tail}</pre>, translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.BULK_STUDENTS_FAILED_TITLE'));
      } else {
        const ok = await askConfirm(
          <div className="space-y-2">
            <p>
              {translate(
                'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_SUCCESS_TEACHERS',
              )}
            </p>
            <p>
              {translate(
                'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NEW_TEACHERS_ALERT',
              )}
            </p>
          </div>,
          {
            title: translate(
              'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE',
            ),
            confirmText: translate('components.BUTTONS.OK') || 'OK',
            cancelText: translate('components.BUTTONS.CANCEL') || 'Cancel',
          },
        );

        if (ok) {
          reloadSoon();
        }
      }
    } finally {
      setProgress('idle');
      setBatchTotal({ total: 0, done: 0 });
    }
  };

  const submitParents = async () => {
    try {
      setProgress('saving');
      // Preflight per-row inline validation
      const preflightErrors = parents.flatMap((p, idx) => {
        const errs = getParentInlineErrors(p);
        return errs.map((e) => `Row ${idx + 1} (UPN ${p.upn || '�'}): ${e}`);
      });
      if (preflightErrors.length) {
        showAlert(
          <pre className="whitespace-pre-wrap text-sm">{preflightErrors.join('\n')}</pre>,
          translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.PARENTS_SOME_REJECTED_TITLE'),
        );
        return;
      }

      // Build clean rows
      const rows: GuardianRow[] = [];
      for (let i = 0; i < parents.length; i++) {
        const p = parents[i];
        const { guardians } = collectGuardians(p);
        rows.push({
          upn: (p.upn ?? '').trim(),
          childId: 0,
          childName: (p.studentName ?? '').trim(),
          guardians: guardians.map((g) => ({ name: (g.name ?? '').trim(), email: (g.email ?? '').trim().toLowerCase() }))
            .filter((g) => g.name || g.email),
        });
      }

      // Batch upload
      const groups = chunk(rows, 10);
      setBatchTotal({ total: rows.length, done: 0 });
      const serverErrors: string[] = [];

      const sendGuardians = async (batch: GuardianRow[]) => {
        const guardiansRepo: any = (repository as any).GuardiansRepository;
        if (guardiansRepo?.bulkUpload) {
          try {
            await guardiansRepo.bulkUpload({ rows: batch });
          } catch (e: any) {
            if (/must be a non-empty array|Value must be an object|Bad Request/i.test(JSON.stringify(e?.response?.data || e?.message || ''))) {
              await guardiansRepo.bulkUpload({ guardians: batch });
            } else {
              throw e;
            }
          }
        } else {
          try {
            await (model as any).Guardians.bulkupload({ rows: batch } as any);
          } catch (eA: any) {
            try {
              await (model as any).Guardians.bulkupload(batch as any);
            } catch (eB: any) {
              const repo2: any = (repository as any).GuardiansRepository;
              if (repo2?.bulkUpload) await repo2.bulkUpload({ rows: batch });
              else throw eB;
            }
          }
        }
      };

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        try {
          await sendGuardians(group);
        } catch (e: any) {
          const status = e?.response?.status;
          const server = e?.response?.data;
          // try to summarise structured context
          if (status === 409 && server?.context) {
            const ctx = server.context as any;
            const push = (msg: string) => serverErrors.push(`Batch ${gi + 1}: ${msg}`);
            (ctx.overLimit ?? []).forEach((x: any) => push(`Over limit: ${x?.upn ?? x}`));
            (ctx.invalidUpns ?? []).forEach((x: any) => push(`Invalid UPN: ${x?.upn ?? x}`));
            (ctx.invalidEmails ?? []).forEach((x: any) => push(`Invalid email: ${x?.email ?? ''} (UPN ${x?.upn ?? ''})`));
            (ctx.duplicateEmails ?? []).forEach((x: any) => push(`Duplicate email: ${x?.email ?? ''} (UPN ${x?.upn ?? ''})`));
          } else {
            serverErrors.push(`Batch ${gi + 1}/${groups.length} failed: ${JSON.stringify(server ?? {}, null, 2)}`);
          }
        } finally {
          setBatchTotal((prev) => ({ ...prev, done: prev.done + group.length }));
        }
      }

      if (serverErrors.length) {
        const header = `${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE')}:\n  ? ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.APPROX')} ${Math.max(0, batchTotal.total - serverErrors.length)} ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.SUCCEEDED')}\n  ? ${serverErrors.length} ${translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NEEDS_ATTENTION')}\n\nDetails:\n`;
        const shown  = serverErrors.slice(0, 200);
        const tail   = serverErrors.length > 200 ? `\nand ${serverErrors.length - 200} more` : '';
        showAlert(<pre className="whitespace-pre-wrap">{header + shown.join('\n') + tail}</pre>, translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.PARENTS_SOME_REJECTED_TITLE'));
      } else {
        showAlert(translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_SUCCESS_PARENTS'), translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPLOAD_FINISHED_TITLE'));
        reloadSoon();
      }
    } finally {
      setProgress('idle');
      setBatchTotal({ total: 0, done: 0 });
    }
  };

  const teacherWindow = useMemo(() => {
    return getWindowedRows(sortedTeachers, vStart, vEnd, editingId);
  }, [ sortedTeachers, vStart, vEnd, editingId ]);

  const parentWindow = useMemo(() => {
    return getWindowedRows(sortedParents, vStart, vEnd, editingId);
  }, [ sortedParents, vStart, vEnd, editingId ]);

  const studentWindow = useMemo(() => {
    return getWindowedRows(students, vStart, vEnd, editingId);
  }, [ students, vStart, vEnd, editingId ]);

  const uploadSummary = useMemo<UploadSummary>(() => {
    if (currentTab === 'students') {
      const rowsForSummary = students.map((student) => studentDrafts[student.id] ?? student);

      return rowsForSummary.reduce<UploadSummary>((summary, student) => {
        const hasErrors = getStudentInlineErrors(student).length > 0;
        if (hasErrors) {
          return { ...summary, total: summary.total + 1, errors: summary.errors + 1 };
        }

        const username = usernameKey(student.username);
        const upn = (student.upn ?? '').trim();
        const isUpdatingStudent = !!upnToUsername[upn] || (!!username && existingUsernames.has(username));

        return {
          ...summary,
          total: summary.total + 1,
          valid: summary.valid + 1,
          newStudents: summary.newStudents + (isUpdatingStudent ? 0 : 1),
          updatingStudents: summary.updatingStudents + (isUpdatingStudent ? 1 : 0),
        };
      }, {
        total: 0,
        valid: 0,
        errors: 0,
        newStudents: 0,
        updatingStudents: 0,
        uploadLabelSingularKey: '',
        uploadLabelPluralKey: '',
      });
    }

    if (currentTab === 'teachers') {
      const errors = teachers.filter((teacher) => !!teacher.error).length;
      return {
        total: teachers.length,
        valid: teachers.length - errors,
        errors,
        newStudents: 0,
        updatingStudents: 0,
        uploadLabelSingularKey: 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.TEACHER_TO_UPLOAD',
        uploadLabelPluralKey: 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.TEACHERS_TO_UPLOAD',
      };
    }

    const errors = parents.filter((parent) => {
      const inlineErrors = getParentInlineErrors(parent);
      const serverErrors = parent.error ? String(parent.error).split(';').map((s) => s.trim()).filter(Boolean) : [];
      return inlineErrors.length + serverErrors.length > 0;
    }).length;

    return {
      total: parents.length,
      valid: parents.length - errors,
      errors,
      newStudents: 0,
      updatingStudents: 0,
      uploadLabelSingularKey: 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.PARENT_ROW_TO_UPLOAD',
      uploadLabelPluralKey: 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.PARENT_ROWS_TO_UPLOAD',
    };
  }, [
    currentTab,
    existingUsernames,
    getParentInlineErrors,
    getStudentInlineErrors,
    parents,
    studentDrafts,
    students,
    teachers,
    upnToUsername,
  ]);

  const studentsTable = useMemo(() => {
    return (
      <>
        <table className="w-full table-auto border-collapse border border-gray-400 text-sm text-center" style={{ minWidth: 1600 }}>
          <thead className="sticky top-0 bg-white z-10">
            <tr>
              <th className="border border-gray-400 text-xs" style={{ width: 40 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ARCHIVE'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ERROR')}</th>
              <th className="border border-gray-400 text-xs" style={{ width: 40 }}>{translate('components.BUTTONS.EDIT')}</th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 60 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 30 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA'} /></th>
              <th className="border border-gray-400 text-xs" style={{ width: 40 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DELETE_ROW_TITLE'} /></th>
            </tr>
          </thead>
          <tbody>
            {studentWindow.topPad > 0 && (
              <tr style={{ height: studentWindow.topPad }}>
                <td colSpan={COLSPAN_STUDENTS} style={{ padding: 0, border: 0 }} />
              </tr>
            )}

            {studentWindow.rows.map((student) => (
              <StudentRow
                key={student.id}
                row={student}
                isEditing={editingId === student.id}
                setEditingRow={setStudentEditingRow}
                existingUsernames={existingUsernames}
                formatYearGroup={formatYearGroup}
                GENDER_OPTIONS={GENDER_OPTIONS}
                normalizeAssessmentDeltaYears={normalizeAssessmentDeltaYears}
                getErrors={getStudentInlineErrors}
                onCommit={commitStudentPatch}
                onDraftChange={handleStudentDraftChange}
                onDelete={deleteStudent}
                upnToUsername={upnToUsername}
                yearGroups={ygList}
                allClasses={suggestedClassNames}
                allTags={allTagNames}
              />

            ))}

            {studentWindow.bottomPad > 0 && (
              <tr style={{ height: studentWindow.bottomPad }}>
                <td colSpan={COLSPAN_STUDENTS} style={{ padding: 0, border: 0 }} />
              </tr>
            )}

          </tbody>
          <tfoot>
            <tr>
              <td colSpan={COLSPAN_STUDENTS} className="text-left p-2">
                <AddRowButton onClick={handleAddRow} />
              </td>
            </tr>
          </tfoot>
        </table>
      </>
    );
  }, [
  // things that actually affect the students table rendering:
    studentWindow,
    editingId,

    existingUsernames,
    formatYearGroup,
    GENDER_OPTIONS,
    normalizeAssessmentDeltaYears,
    getStudentInlineErrors,
    commitStudentPatch,
    handleStudentDraftChange,
    setStudentEditingRow,
    deleteStudent,

    upnToUsername,
    ygList,
    suggestedClassNames,
    allTagNames,
    handleAddRow,
    translate,
  ]);


  return (

    <div className="p-4 rounded-md border-2 border-yellow-400 shadow-md bg-white" >
      <div className="p-4">
        <div className="mb-0 flex gap-2">

        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        {/* Hidden but accessible file input */}
        <input
          id="bulk-upload-file"
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={onPickFile}
          aria-label={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.UPLOAD_CSV')}
          className="sr-only"
        />

        {/* Whizz-styled trigger button that triggers above upload */}
        <Button
          data-id="whizz_button"
          color="positive"
          isOutline
          className="!h-8 !py-0 !px-3"
          onClick={() => fileInputRef.current?.click()}
          disabled={submitting || isBusy}
        >
          {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.UPLOAD_CSV')}
        </Button>

        {/* Optional: show picked filename */}
        {selectedFileName && (
          <span className="text-sm text-gray-600 truncate max-w-[24rem]">
            {selectedFileName}
          </span>
        )}

      </div>



      <div className="rounded-md border border-gray-300 bg-white mt-2">
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]" ref={scrollRef} style={{ overflowAnchor: 'none' }}>

          {currentTab === 'students' && studentsTable}

          {currentTab === 'teachers' && (
            <table className="w-full table-auto border-collapse border border-gray-400 text-sm text-center" style={{ minWidth: 1600 }}>
              <thead>
                <tr>
                  <th className="border border-gray-400" style={{ width: 160 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ERROR'} /></th>
                  <th className="border border-gray-400" style={{ width: 60 }}><TranslateWithSpan i18nKey={'components.BUTTONS.EDIT'} /></th>
                  <th className="border border-gray-400" style={{ width: 220 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL_CLASSES'} /></th>
                  <th className="border border-gray-400" style={{ width: 60 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN'} /></th>
                  <th className="border border-gray-400" style={{ width: 60 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ACTIONS'} /></th>
                </tr>
              </thead>
              <tbody>
                {teacherWindow.topPad > 0 && (
                  <tr style={{ height: teacherWindow.topPad }}>
                    <td colSpan={COLSPAN_TEACHERS} style={{ padding: 0, border: 0 }} />
                  </tr>
                )}

                {teacherWindow.rows.map((teacher) => {
                  const isEditing = editingId === teacher.id;
                  const badEmail = !isValidEmail(teacher.email);
                  const badRecovery = !isValidEmail(teacher.RecoveryEmail, { allowEmpty: true });
                  const isCurrentTeacher = !!currentTeacherEmail && normalizeEmail(teacher.email) === currentTeacherEmail;

                  return (
                    <TeacherRow
                      key={teacher.id}
                      teacher={teacher}
                      isEditing={isEditing}
                      badEmail={badEmail}
                      badRecovery={badRecovery}
                      isCurrentTeacher={isCurrentTeacher}
                      allClassNames={suggestedClassNames}
                      onEditChange={handleTeacherEditChange}
                      onToggleAdmin={handleTeacherToggleAdmin}
                      onSetEditingId={setEditingId}
                      onDelete={confirmDeleteTeacher}
                    />
                  );
                })}

                {teacherWindow.bottomPad > 0 && (
                  <tr style={{ height: teacherWindow.bottomPad }}>
                    <td colSpan={COLSPAN_TEACHERS} style={{ padding: 0, border: 0 }} />
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={COLSPAN_TEACHERS} className="text-left p-2">
                    <AddRowButton onClick={handleAddRow} />
                  </td>
                </tr>
              </tfoot>
            </table>

          )}

          {currentTab === 'parents' && (
            <table className="w-full table-fixed border-collapse border border-gray-400 text-sm text-center">
              <thead>
                <tr>
                  <th className="border border-gray-400" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.ERROR'} /></th>
                  <th className="border border-gray-400" style={{ width: 80 }}><TranslateWithSpan i18nKey={'components.BUTTONS.EDIT'} /></th>
                  <th className="border border-gray-400" style={{ width: 180 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN'} /></th>
                  <th className="border border-gray-400" style={{ width: 80 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 200 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 200 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL'} /></th>
                  <th className="border border-gray-400" style={{ width: 120 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME'} /></th>
                  <th className="border border-gray-400" style={{ width: 200 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL'} /></th>
                  <th className="border border-gray-400" style={{ width: 140 }}><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.ACTIONS'} /></th>
                </tr>
              </thead>
              <tbody>
                {parentWindow.topPad > 0 && (
                  <tr style={{ height: parentWindow.topPad }}>
                    <td colSpan={COLSPAN_PARENTS} style={{ padding: 0, border: 0 }} />
                  </tr>
                )}

                {parentWindow.rows.map((parent) => {
                  const isEditing = editingId === parent.id;

                  // inline + server errors combined
                  const inlineErrs = getParentInlineErrors(parent);
                  const mergedErrs = Array.from(
                    new Set([
                      ...inlineErrs,
                      ...(parent.error ? String(parent.error).split(';').map((s) => s.trim()).filter(Boolean) : []),
                    ]),
                  );

                  const bad1 = !!parent.g1Email && !isValidEmail(parent.g1Email, { allowEmpty: true });
                  const bad2 = !!parent.g2Email && !isValidEmail(parent.g2Email, { allowEmpty: true });
                  const bad3 = !!parent.g3Email && !isValidEmail(parent.g3Email, { allowEmpty: true });
                  const errorText = mergedErrs.join('; ');

                  return (
                    <ParentRow
                      key={parent.id}
                      parent={parent}
                      isEditing={isEditing}
                      errorText={errorText}
                      bad1={bad1}
                      bad2={bad2}
                      bad3={bad3}
                      onEditChange={handleParentEditChange}
                      onSetEditingId={setEditingId}
                      onDelete={confirmDeleteParent}
                    />
                  );
                })}

                {parentWindow.bottomPad > 0 && (
                  <tr style={{ height: parentWindow.bottomPad }}>
                    <td colSpan={COLSPAN_PARENTS} style={{ padding: 0, border: 0 }} />
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={COLSPAN_PARENTS} className="text-left p-2">
                    <AddRowButton onClick={handleAddRow} />
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      {uploadSummary.total > 0 && (
        <div className="fixed left-0 bottom-0 z-40 w-full min-h-[125px] flex items-center justify-center px-4 py-5" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
          <div className="flex flex-wrap items-center justify-center gap-4 bg-white rounded-default py-5 px-8 shadow-lg">
            {uploadSummary.errors > 0 && (
              <div className="min-w-[120px] flex items-center gap-2">
                <div className="inline-flex items-center justify-center min-w-[34px] h-8 px-2 rounded-default bg-error text-black text-2xl font-bold leading-none">{uploadSummary.errors}</div>
                <div className="text-base font-semibold text-gray-600">
                  {translate(uploadSummary.errors === 1
                    ? 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.ERROR'
                    : 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.ERRORS')}
                </div>
              </div>
            )}

            {currentTab === 'students' && uploadSummary.newStudents > 0 && (
              <div className="min-w-[120px] flex items-center gap-2">
                <div className="inline-flex items-center justify-center min-w-[34px] h-8 px-2 rounded-default bg-positive text-black text-2xl font-bold leading-none">{uploadSummary.newStudents}</div>
                <div className="text-base font-semibold text-gray-600">
                  {translate(uploadSummary.newStudents === 1
                    ? 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.NEW_STUDENT'
                    : 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.NEW_STUDENTS')}
                </div>
              </div>
            )}

            {currentTab !== 'students' && (
              <div className="min-w-[150px] flex items-center gap-2">
                <div className="inline-flex items-center justify-center min-w-[34px] h-8 px-2 rounded-default bg-positive text-black text-2xl font-bold leading-none">{uploadSummary.valid}</div>
                <div className="text-base font-semibold text-gray-600">
                  {translate(uploadSummary.valid === 1
                    ? uploadSummary.uploadLabelSingularKey
                    : uploadSummary.uploadLabelPluralKey)}
                </div>
              </div>
            )}

            {currentTab === 'students' && uploadSummary.updatingStudents > 0 && (
              <div className="min-w-[120px] flex items-center gap-2">
                <div className="inline-flex items-center justify-center min-w-[34px] h-8 px-2 rounded-default bg-positive text-black text-2xl font-bold leading-none">{uploadSummary.updatingStudents}</div>
                <div className="text-base font-semibold text-gray-600">
                  {translate(uploadSummary.updatingStudents === 1
                    ? 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.UPDATING_STUDENT'
                    : 'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.SUMMARY.UPDATING_STUDENTS')}
                </div>
              </div>
            )}

            <Button
              data-id="whizz_button"
              color="positive"
              isOutline
              className="disabled:opacity-60 !h-10 !py-0 !px-5 min-w-[120px] text-base"
              onClick={handleSubmit}
              disabled={submitting || uploadSummary.valid === 0 || uploadSummary.errors > 0}
              icon={!submitting ? GLOBAL_ICONS.save : undefined}
              aria-label={submitting
                ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVING')
                : translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVE')}
              title={submitting
                ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVING')
                : translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVE')}
            >
              {submitting
                ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVING')
                : translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.SAVE')}
            </Button>
          </div>
        </div>
      )}
      {isBusy && (
        <Modal open toggle={noop} onClose={noop} disableMaskClick alwaysOnTop size="sm">
          <div className="p-5 flex items-center gap-3">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
            <span className="font-medium">{busyText}</span>
          </div>
        </Modal>
      )}


      {/* Alert modal */}
      {ui.alert.open && (
        <Modal
          open
          toggle={closeAlert}
          onClose={closeAlert}
          disableMaskClick
          alwaysOnTop
          size="md"
        >
          <div className="p-0">
            <div className="px-4 pt-4">
              {ui.alert.title && <h3 className="text-lg font-semibold mb-2">{ui.alert.title}</h3>}
            </div>

            {/* Scrollable body */}
            <div className="px-4 pb-3 max-h-[70vh] overflow-auto">
              <div className="prose prose-sm max-w-none">
                {/* If body contains <pre>, make it wrap nicely */}
                <div className="[&>pre]:whitespace-pre-wrap [&>pre]:break-words [&>pre]:text-sm">
                  {ui.alert.body}
                </div>
              </div>
            </div>

            <ModalFooter justify="end" className="px-4 pb-4">
              {hasUsernameSuggestions && (
                <Button
                  data-id="whizz_button"
                  color="positive"
                  isOutline
                  className="!h-8 !py-0 !px-3 mr-2"
                  onClick={() => {
                    applyAllUsernameSuggestions();
                    clearGridExceptAppliedUsernames();
                    closeAlert();
                  }}
                >
                  {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.APPLY_SUGGESTED_USERNAMES')}
                </Button>
              )}
              <Button
                data-id="whizz_button"
                color="positive"
                isOutline
                className="!h-8 !py-0 !px-3"
                onClick={closeAlert}
              >
                {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.OK')}
              </Button>
            </ModalFooter>
          </div>
        </Modal>
      )}


      {/* Confirm modal */}
      {ui.confirm.open && (
        <Modal
          open
          toggle={() => answerConfirm(false)}
          onClose={() => answerConfirm(false)}
          disableMaskClick
          alwaysOnTop
          size="md"
        >
          <div className="p-0">
            <div className="px-4 pt-4">
              {ui.confirm.title && <h3 className="text-lg font-semibold mb-2">{ui.confirm.title}</h3>}
            </div>

            {/* Scrollable body */}
            <div className="px-4 pb-3 max-h-[70vh] overflow-auto">
              <div className="prose prose-sm max-w-none">
                <div className="[&>pre]:whitespace-pre-wrap [&>pre]:break-words [&>pre]:text-sm">
                  {ui.confirm.body}
                </div>
              </div>
            </div>

            <ModalFooter justify="end" className="px-4 pb-4">
              <Button
                data-id="whizz_button"
                color="positive"
                isOutline
                className="!h-8 !py-0 !px-3 mr-2"
                onClick={() => answerConfirm(false)}
              >
                {ui.confirm.opts.cancelText ?? translate('components.BUTTONS.CANCEL')}
              </Button>
              <Button
                data-id="whizz_button"
                color={ui.confirm.opts.destructive ? 'negative' : 'positive'}
                isOutline
                className="!h-8 !py-0 !px-3"
                onClick={() => answerConfirm(true)}
              >
                {ui.confirm.opts.confirmText ?? 'OK'}
              </Button>
            </ModalFooter>
          </div>

        </Modal>
      )}


    </div>
  );
};

const TeacherRow = memo(function TeacherRow({
  teacher,
  isEditing,
  badEmail,
  badRecovery,
  isCurrentTeacher,
  allClassNames,
  onEditChange,
  onToggleAdmin,
  onSetEditingId,
  onDelete,
}: {
  teacher: Teacher;
  isEditing: boolean;
  badEmail: boolean;
  badRecovery: boolean;
  isCurrentTeacher: boolean;
  allClassNames: string[];
  onEditChange: (id: string, key: keyof Teacher, value: string) => void;
  onToggleAdmin: (id: string, checked: boolean) => void;
  onSetEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  onDelete: (id: string) => void | Promise<void>;
}) {
  const { translate } = useI18n();
  const jobTypeLookups = useMemo<JobTypeLookups>(
    () => buildJobTypeLookups((key, fallback) => translate(key, fallback)),
    [ translate ],
  );

  return (
    <tr style={{ height: `${ROW_HEIGHT}px`, backgroundColor: isEditing ? ROW_EDIT_BACKGROUND_COLOR : teacher.error ? ROW_ERROR_BACKGROUND_COLOR : undefined }}>
      <td className="border border-gray-400 text-black text-xs font-semibold px-1">{teacher.error}</td>

      <td className="border border-gray-400">
        {isEditing ? (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className={ICON_BTN}
            icon={GLOBAL_ICONS.save}
            onClick={() => onSetEditingId(null)}
            title={translate('components.BUTTONS.SAVE')}
          >
            <span className="sr-only">{translate('components.BUTTONS.SAVE')}</span>
          </Button>
        ) : (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className={ICON_BTN}
            icon={GLOBAL_ICONS.edit}
            onClick={() => onSetEditingId(teacher.id)}
            title={translate('components.BUTTONS.EDIT')}
          >
            <span className="sr-only">{translate('components.BUTTONS.EDIT')}</span>
          </Button>
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <select
            className={INPUT_SM}
            value={String(jobTypeIdFromInput(teacher.jobType, jobTypeLookups))}
            onChange={(e) => {
              const id = Number(e.target.value);
              const label = jobTypeLookups.labelById[id] ?? '';
              onEditChange(teacher.id, 'jobType', label);
            }}
          >
            {jobTypeLookups.jobTypes.map((jt) => (
              <option key={jt.id} value={String(jt.id)}>{jt.label}</option>
            ))}
          </select>
        ) : (
          teacher.jobType || ''
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full px-1"
            value={teacher.name}
            onChange={(e) => onEditChange(teacher.id, 'name', e.target.value)}
          />
        ) : (
          teacher.name
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full px-1"
            value={teacher.lastName}
            onChange={(e) => onEditChange(teacher.id, 'lastName', e.target.value)}
          />
        ) : (
          teacher.lastName
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <div className="flex items-center h-6">
            <input
              className={`w-full text-sm px-1 py-0.5 h-6 border ${badEmail ? 'border-red-500' : 'border-gray-300'}`}
              type="email"
              inputMode="email"
              value={teacher.email}
              onChange={(e) => onEditChange(teacher.id, 'email', e.target.value)}
              title={badEmail ? 'Enter a valid email like name@example.com' : ''}
            />
            {badEmail && <span className="ml-1 text-xs leading-none" title="Invalid email">?</span>}
          </div>
        ) : (
          <>
            {teacher.email}
          </>
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <div className="flex items-center h-6">
            <input
              className={`w-full text-sm px-1 py-0.5 h-6 border ${badRecovery ? 'border-red-500' : 'border-gray-300'}`}
              type="email"
              inputMode="email"
              value={teacher.RecoveryEmail}
              onChange={(e) => onEditChange(teacher.id, 'RecoveryEmail', e.target.value)}
              title={badRecovery ? 'Enter a valid email like name@example.com' : ''}
            />
            {badRecovery && <span className="ml-1 text-xs leading-none" title="Invalid recovery email">?</span>}
          </div>
        ) : (
          <>
            {teacher.RecoveryEmail}
            {badRecovery && <span className="ml-1 text-xs" title="Invalid recovery email">?</span>}
          </>
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing
          ? <input className="w-full px-1" value={teacher.phone} onChange={(e) => onEditChange(teacher.id, 'phone', e.target.value)} />
          : teacher.phone}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <TagInput
            value={teacher.classes}
            allTags={allClassNames}
            onChange={(next) => onEditChange(teacher.id, 'classes', next)}
            placeholder="Type class� "
            appendCommaOnPick
          />
        ) : (
          teacher.classes
        )}
      </td>
      <td className="border border-gray-400">
        {isEditing ? (
          <TagInput
            value={teacher.emailClasses}
            allTags={allClassNames}
            onChange={(next) => onEditChange(teacher.id, 'emailClasses', next)}
            placeholder="Type class� "
            appendCommaOnPick
          />
        ) : (
          teacher.emailClasses
        )}
      </td>
      <td className="border border-gray-400">
        {isCurrentTeacher ? (
          <span title="Can't remove admin from the logged-in user">
            {isEditing
              ? <input type="checkbox" className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300" checked={teacher.admin} disabled onChange={(e) => onToggleAdmin(teacher.id, e.target.checked)} />
              : <input type="checkbox" checked={teacher.admin} readOnly disabled />}
          </span>
        ) : (
          <input
            type="checkbox"
            className={isEditing ? 'w-full text-sm px-1 py-0.5 h-6 border border-gray-300' : undefined}
            checked={teacher.admin}
            onChange={(e) => onToggleAdmin(teacher.id, e.target.checked)}
          />
        )}
      </td>
      <td className="border border-gray-400">
        <DeleteRowButton rowId={teacher.id} onDelete={onDelete} />
      </td>
    </tr>
  );
});

const ParentRow = memo(function ParentRow({
  parent,
  isEditing,
  errorText,
  bad1,
  bad2,
  bad3,
  onEditChange,
  onSetEditingId,
  onDelete,
}: {
  parent: Parent;
  isEditing: boolean;
  errorText: string;
  bad1: boolean;
  bad2: boolean;
  bad3: boolean;
  onEditChange: (id: string, key: keyof Parent, value: string) => void;
  onSetEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  onDelete: (id: string) => void | Promise<void>;
}) {
  const { translate } = useI18n();

  return (
    <tr style={{ height: `${ROW_HEIGHT}px`, backgroundColor: isEditing ? ROW_EDIT_BACKGROUND_COLOR : errorText ? ROW_ERROR_BACKGROUND_COLOR : undefined }}>
      <td className="border border-gray-400 text-black text-xs font-semibold px-1">
        {errorText}
      </td>

      <td className="border border-gray-400">
        {isEditing ? (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className={ICON_BTN}
            icon={GLOBAL_ICONS.save}
            onClick={() => onSetEditingId(null)}
            title={translate('components.BUTTONS.SAVE')}
          >
            <span className="sr-only">{translate('components.BUTTONS.SAVE')}</span>
          </Button>
        ) : (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className={ICON_BTN}
            icon={GLOBAL_ICONS.edit}
            onClick={() => onSetEditingId(parent.id)}
            title={translate('components.BUTTONS.EDIT')}
          >
            <span className="sr-only">{translate('components.BUTTONS.EDIT')}</span>
          </Button>
        )}
      </td>

      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="border px-1 w-full"
            value={parent.upn}
            onChange={(e) => onEditChange(parent.id, 'upn', e.target.value)}
          />
        ) : (
          parent.upn
        )}
      </td>

      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="border px-1 w-full"
            value={parent.studentName}
            onChange={(e) => onEditChange(parent.id, 'studentName', e.target.value)}
          />
        ) : (
          parent.studentName
        )}
      </td>

      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="border px-1 w-full"
            value={parent.g1Name}
            onChange={(e) => onEditChange(parent.id, 'g1Name', e.target.value)}
          />
        ) : (
          parent.g1Name
        )}
      </td>

      <EmailCell
        rowId={parent.id}
        isEditing={isEditing}
        value={parent.g1Email}
        field="g1Email"
        bad={bad1}
        title="Invalid email 1"
        onChange={onEditChange}
        onBlurNormalize={(id, field, v) =>
          onEditChange(id, field, normalizeEmail(v))
        }
      />

      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="border px-1 w-full"
            value={parent.g2Name}
            onChange={(e) => onEditChange(parent.id, 'g2Name', e.target.value)}
          />
        ) : (
          parent.g2Name
        )}
      </td>

      <EmailCell
        rowId={parent.id}
        isEditing={isEditing}
        value={parent.g2Email}
        field="g2Email"
        bad={bad2}
        title="Invalid email 2"
        onChange={onEditChange}
        onBlurNormalize={(id, field, v) =>
          onEditChange(id, field, normalizeEmail(v))
        }
      />

      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="border px-1 w-full"
            value={parent.g3Name}
            onChange={(e) => onEditChange(parent.id, 'g3Name', e.target.value)}
          />
        ) : (
          parent.g3Name
        )}
      </td>

      <EmailCell
        rowId={parent.id}
        isEditing={isEditing}
        value={parent.g3Email}
        field="g3Email"
        bad={bad3}
        title="Invalid email 3"
        onChange={onEditChange}
        onBlurNormalize={(id, field, v) =>
          onEditChange(id, field, normalizeEmail(v))
        }
      />

      <td className="border border-gray-400">
        <DeleteRowButton rowId={parent.id} onDelete={onDelete} />
      </td>
    </tr>
  );
});

const StudentRow = memo(function StudentRow({
  row,
  isEditing,
  setEditingRow,
  existingUsernames,
  yearGroups,
  formatYearGroup,
  GENDER_OPTIONS,
  normalizeAssessmentDeltaYears,
  getErrors,
  onCommit,
  onDraftChange,
  onDelete,
  upnToUsername,
  allClasses,
  allTags,
}: {
  row: Student;
  isEditing: boolean;
  setEditingRow: (id: string | null) => void;
  existingUsernames: Set<string>;
  yearGroups: YearGroup[];
  allClasses: string[];
  allTags: string[];
  formatYearGroup: (raw?: string | null) => string;
  GENDER_OPTIONS: { value: string; label: string }[];
  normalizeAssessmentDeltaYears: (v?: string | number | null) => '' | string;
  getErrors: (s: Student) => string[];
  onCommit: (id: string, patch: Partial<Student>) => void;
  onDraftChange: (id: string, draft: Student | null) => void;
  onDelete: (id: string) => void;
  upnToUsername: Record<string, string>;
}) {

  const { translate } = useI18n();
  const [ draft, setDraft ] = useState(row);
  const wasEditingRef = useRef(false);

  useEffect(() => {
    if (isEditing && !wasEditingRef.current) setDraft(row);
    wasEditingRef.current = isEditing;
  }, [ isEditing ]);

  useEffect(() => {
    if (!isEditing) {
      onDraftChange(row.id, null);
      return;
    }

    onDraftChange(row.id, draft);
  }, [
    isEditing,
    onDraftChange,
    row.id,
    draft.archive,
    draft.upn,
    draft.firstName,
    draft.lastName,
    draft.dateOfBirth,
    draft.yearGroup,
    draft.gender,
    draft.password,
    draft.username,
    draft.licenseActive,
    draft.className,
    draft.tagName,
    draft.aboveBelowAssessmentDelta,
    draft.assessmentDelta,
  ]);

  const commit = useCallback(() => {
    if (draft === row) return;
    // shallow compare only the fields we edit to avoid unnecessary parent updates
    const changed = Object.keys(draft).some((k) => (draft as any)[k] !== (row as any)[k]);
    if (changed) onCommit(row.id, draft);
  }, [ draft, row, onCommit ]);

  const draftErrors = useMemo(
    () => getErrors(draft),
    [
      getErrors,
      draft.upn, draft.firstName, draft.lastName, draft.dateOfBirth,
      draft.yearGroup, draft.gender, draft.password, draft.username, draft.className,
      draft.tagName, draft.assessmentDelta,
    ],
  );

  return (
    <tr style={{ height: `${ROW_HEIGHT}px`, backgroundColor: isEditing ? ROW_EDIT_BACKGROUND_COLOR : draftErrors.length ? ROW_ERROR_BACKGROUND_COLOR : undefined }}>

      {/* Archive */}
      <td className="border border-gray-400">
        <input
          type="checkbox"
          className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
          checked={isEditing ? !!draft.archive : !!row.archive}
          onChange={(e) => {
            const checked = e.target.checked;

            if (isEditing) {
              // While editing, update the draft; it'll be committed on save
              setDraft((d) => ({ ...d, archive: checked }));
            } else {
              // When not editing, commit immediately so no edit-mode is needed
              onCommit(row.id, { archive: checked });
            }
          }}
          title="Archive this student"
        />
      </td>

      {/* Errors */}
      <td className="border border-gray-400 text-black font-semibold text-xs px-1">
        <RowErrorsMemo errors={draftErrors} />
      </td>

      {/* Edit/Save */}
      <td className="border border-gray-400">
        {isEditing ? (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className="!h-8 !w-8 !p-0 grid place-items-center rounded"
            icon={GLOBAL_ICONS.save}
            onClick={() => { commit(); setEditingRow(null); }}
            title="Save"
          />
        ) : (
          <Button
            data-id="whizz_button"
            color={'transparent'}
            className="!h-8 !w-8 !p-0 grid place-items-center rounded"
            icon={GLOBAL_ICONS.edit}
            onClick={() => setEditingRow(row.id)}
            title="Edit"
          />
        )}
      </td>

      {/* UPN */}
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
            value={draft.upn}
            onChange={(e) => setDraft((d) => ({ ...d, upn: e.target.value }))}
            onBlur={commit}
          />
        ) : draft.upn}
      </td>

      {/* First Name */}
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
            value={draft.firstName}
            onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
            onBlur={commit}
          />
        ) : draft.firstName}
      </td>

      {/* Last Name */}
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
            value={draft.lastName}
            onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
            onBlur={commit}
          />
        ) : draft.lastName}
      </td>

      {/* DOB */}
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
            value={draft.dateOfBirth}
            onChange={(e) => setDraft((d) => ({ ...d, dateOfBirth: e.target.value }))}
            onBlur={commit}
          />
        ) : draft.dateOfBirth}
      </td>

      {/* Gender */}
      <td className="border border-gray-400">
        {isEditing ? (
          <select
            className="w-full text-sm border border-gray-300 h-6 leading-6 px-2 py-0"
            value={normalizeGenderUI(draft.gender)}
            onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}
            onBlur={commit}
          >
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : draft.gender}
      </td>

      {/* Username */}
      <td className="border border-gray-400 p-0">
        {(() => {
          const unameKey = (draft.username ?? '').trim().toLowerCase();
          const upnKey = (draft.upn ?? '').trim();
          const mappedOwner = upnKey ? upnToUsername[upnKey] : undefined;
          const hasUpnConflict = !!upnKey && !!mappedOwner && mappedOwner !== unameKey;
          const existsInSchool = !!unameKey && existingUsernames.has(unameKey);

          let chipText: string | null = null;
          let chipClass = 'inline-block text-[11px] leading-4 px-1.5 rounded border align-middle';

          if (draft.username) {
            if (hasUpnConflict) {
              chipText = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPN_USED_BY')
    + (mappedOwner ? `: ${mappedOwner}` : '');
              chipClass += ' bg-amber-100 text-amber-800 border-amber-200';
            } else if (existsInSchool) {

              chipText = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.EXISTING_IN_SCHOOL', 'Existing in school');
              chipClass += ' bg-blue-100 text-blue-800 border-blue-200';
            } else {

              chipText = translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.NEW_IN_SCHOOL');
              chipClass += ' bg-green-100 text-green-800 border-green-200';
            }
          }

          return (
            <div className={`p-1 text-left ${draft.username
              ? (hasUpnConflict ? 'bg-amber-50' : (existsInSchool ? 'bg-blue-50' : 'bg-green-50'))
              : ''}`}>
              {isEditing ? (
                <input
                  className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
                  value={draft.username}
                  onChange={(e) => setDraft((d) => ({ ...d, username: e.target.value }))}
                  onBlur={commit}
                />
              ) : (
                <div className="px-1 py-0.5 truncate">{draft.username}</div>
              )}

              {chipText && (
                <div className="mt-0.5">
                  <span className={chipClass} title={hasUpnConflict ? translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.UPN_ALREADY_TAKE') : ''}>
                    {chipText}
                  </span>
                </div>
              )}
            </div>
          );
        })()}
      </td>

      {/* Password */}
      <td className="border border-gray-400">
        {isEditing ? (
          <input
            className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
            value={draft.password}
            onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
            onBlur={commit}
          />
        ) : draft.password}
      </td>

      {/* License */}
      <td className="border border-gray-400">
        <input
          type="checkbox"
          className="w-full text-sm px-1 py-0.5 h-6 border border-gray-300"
          checked={isEditing ? !!draft.licenseActive : !!row.licenseActive}
          onChange={(e) => {
            const checked = e.target.checked;

            if (isEditing) {
              // Normal editing flow: update the draft, commit on blur/save
              setDraft((d) => ({ ...d, licenseActive: checked }));
            } else {
              // Non-editing flow: commit immediately to the parent state
              onCommit(row.id, { licenseActive: checked });
            }
          }}
          title="Toggle license"
        />
      </td>

      {/* Year Group */}
      <td className="border border-gray-400">
        {isEditing && yearGroups.length > 0 ? (
          <select
            className="w-full text-sm border border-gray-300 h-6 leading-6 px-2 py-0"
            value={draft.yearGroup}
            onChange={(e) => setDraft((d) => ({ ...d, yearGroup: e.target.value }))}
            onBlur={commit}
          >
            <option value=""></option>
            {yearGroups.map((yg) => (
              <option key={yg.id} value={yg.code}>{yg.name}</option>
            ))}
          </select>
        ) : (
          formatYearGroup(draft.yearGroup)
        )}
      </td>

      {/* Class */}
      <td className="border border-gray-400">
        {isEditing ? (
          <TagInput
            value={draft.className}
            allTags={allClasses}
            onChange={(next) => setDraft((d) => ({ ...d, className: next }))}
            appendCommaOnPick
            onCommit={commit}
          />
        ) : draft.className}
      </td>

      {/* Tag */}
      <td className="border border-gray-400">
        {isEditing ? (
          <TagInput
            value={draft.tagName}
            allTags={allTags}
            onChange={(next) => setDraft((d) => ({ ...d, tagName: next }))}
            appendCommaOnPick
            onCommit={commit}
          />
        ) : draft.tagName}
      </td>

      {/* Assessment Delta */}
      <td className="border border-gray-400">
        {isEditing ? (
          <TagInput
            value={normalizeAssessmentDeltaYears(draft.assessmentDelta)}
            allTags={ASSESSMENT_DELTA_OPTIONS}
            onChange={(next) => {
              if (next === '' || /^(10|[1-9])$/.test(next.trim())) {
                setDraft((d) => ({ ...d, assessmentDelta: next.trim() }));
              }
            }}
            placeholder="1-10"
            onCommit={commit}
          />
        ) : (
          normalizeAssessmentDeltaYears(draft.assessmentDelta)
        )}
      </td>

      {/* Delete */}
      <td className="border border-gray-400">
        <DeleteRowButton rowId={row.id} onDelete={onDelete} />
      </td>
    </tr>
  );
});
export default StudentUploadPage;

